import { storageKey, ScopedStorage } from '@authe/fe-commons/storage';
import { createLogger } from '@authe/fe-commons/logging';
import * as abi_star from '@authe/fe-commons/abi';
import { unwrapData, ApiError } from '@authe/fe-commons/api';
import * as contracts_star from '@authe/fe-commons/contracts';
import { scopeRungLabel, directChain, ProxyKind, proxyOverrideRequestBody, windowCovers, toProvenance, formatBlockWindow, windowViewBlock } from '@authe/fe-commons/contracts';
export { scopeRungLabel } from '@authe/fe-commons/contracts';
import { ContractStateResultSchema, TransactionStateInsightSchema } from '@authe/fe-commons/models';
import * as React19 from 'react';
import { createRpcContractReader, useContractWrite } from '@authe/web3';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useDepsChanged } from '@authe/ui-core/api';
import { parseAbi } from 'viem';
import { EmptyState, Accordion, AccordionItem, AccordionTrigger, Badge, AccordionContent, cn, Field, Input, JsonEditor, Alert, AlertDescription, Button, NativeSelect, Separator, Label, Switch, Skeleton, ErrorState, Spinner, AddressDisplay, useScopedOverride, notifySuccess, notifyError, BlockPinControl, WhyPanel, AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, Textarea, ScrollArea, CompareView, Tabs, TabsList, TabsTrigger, TabsContent, CopyButton, Card, CardContent, notifyWarning, notifyInfo, CardHeader, ChainAvatar, CardTitle, WhyPopover, Table, TableHeader, TableRow, TableHead, TableBody, TableCell, HashDisplay } from '@authe/ui-core';
import { isEVM } from '@authe/fe-commons/chains';

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __getOwnPropNames = Object.getOwnPropertyNames;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __copyProps = (to, from, except, desc) => {
  if (from && typeof from === "object" || typeof from === "function") {
    for (let key of __getOwnPropNames(from))
      if (!__hasOwnProp.call(to, key) && key !== except)
        __defProp(to, key, { get: () => from[key], enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable });
  }
  return to;
};
var __reExport = (target, mod, secondTarget) => (__copyProps(target, mod, "default"), secondTarget);
var CONTRACT_EXPLORER_PACKAGE = "contract-explorer";
var ABI_OPTIONS_KEY = storageKey(CONTRACT_EXPLORER_PACKAGE, "abi-options");
var PINNED_ABIS_KEY = storageKey(CONTRACT_EXPLORER_PACKAGE, "pinned-abis");
var INLINE_ABIS_KEY = storageKey(CONTRACT_EXPLORER_PACKAGE, "inline-abis");
var ABI_OPTIONS_NAME = `${CONTRACT_EXPLORER_PACKAGE}:abi-options`;
var PINNED_ABIS_NAME = `${CONTRACT_EXPLORER_PACKAGE}:pinned-abis`;
var INLINE_ABIS_NAME = `${CONTRACT_EXPLORER_PACKAGE}:inline-abis`;
var ABI_CACHE_MAX_ENTRIES = 64;
var ABI_CACHE_TTL_MS = 5 * 6e4;
var EVENT_LOG_CACHE_MAX_ENTRIES = 48;
var EVENT_LOG_CACHE_TTL_MS = 6e4;
var PINNED_ABIS_LIMIT = 8;

// src/model/options.ts
var AbiSourceKind = {
  /** User-supplied / edited JSON ABI. */
  INLINE: "inline",
  /** Previously resolved + cached ABI (in-memory or scoped storage). */
  CACHE: "cache",
  /** Backend catalog ABI, resolved through org/tenant/user precedence. */
  CATALOG: "catalog",
  /** Well-known default ABIs (ERC-20/721/1155, EIP-1967, …). */
  DEFAULTS: "defaults",
  /** External explorer sources (Etherscan-like). */
  EXTERNAL: "external"
};
var ALL_ABI_SOURCES = [
  AbiSourceKind.INLINE,
  AbiSourceKind.CACHE,
  AbiSourceKind.CATALOG,
  AbiSourceKind.DEFAULTS,
  AbiSourceKind.EXTERNAL
];
var DEFAULT_ABI_FETCH_OPTIONS = {
  useDefaults: true,
  useExternalSources: false,
  useCatalog: true,
  useProxies: true,
  discoverProxies: true,
  sourcePriority: [
    AbiSourceKind.INLINE,
    AbiSourceKind.CACHE,
    AbiSourceKind.CATALOG,
    AbiSourceKind.DEFAULTS,
    AbiSourceKind.EXTERNAL
  ]
};
var PROVIDER_ABI_SOURCES = [
  AbiSourceKind.CATALOG,
  AbiSourceKind.DEFAULTS,
  AbiSourceKind.EXTERNAL
];
function isProviderSource(source) {
  return PROVIDER_ABI_SOURCES.includes(source);
}
function providerSourceOrder(priority) {
  const kept = priority.filter(isProviderSource);
  const seen = new Set(kept);
  return [...kept, ...PROVIDER_ABI_SOURCES.filter((s) => !seen.has(s))];
}
function withProviderOrder(priority, order) {
  const head = priority.filter((s) => !isProviderSource(s));
  return [...head, ...order];
}
function moveAbiSource(priority, source, delta) {
  const order = providerSourceOrder(priority);
  const from = order.indexOf(source);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= order.length) return withProviderOrder(priority, order);
  const next = [...order];
  next[from] = order[to];
  next[to] = order[from];
  return withProviderOrder(priority, next);
}
function optionsKey(opts) {
  return [
    opts.useDefaults ? "d" : "",
    opts.useExternalSources ? "x" : "",
    opts.useCatalog ? "c" : "",
    opts.useProxies ? "p" : "",
    opts.discoverProxies ? "P" : "",
    opts.sourcePriority.join(">")
  ].join("|");
}

// src/model/contractStateRequest.ts
function blockToBlockSpec(block) {
  if (!block) return void 0;
  if (block.blockNumber != null) {
    const n = Number(block.blockNumber);
    if (Number.isSafeInteger(n) && n >= 0) return { number: n, block_hash: null, timestamp: null, tag: null };
    throw new RangeError("Block number must be a nonnegative safe integer");
  }
  if (block.blockTag === "pending") throw new Error("Pending state cannot provide a coherent contract snapshot");
  if (block.blockTag) return { number: null, block_hash: null, timestamp: null, tag: block.blockTag };
  return void 0;
}
function buildContractStateRequest(params) {
  const target = {
    address: params.address,
    name: null,
    category: params.category ?? null,
    variables: params.variables ?? [],
    use_category_defaults: params.useCategoryDefaults ?? true,
    auto_detect_proxy: params.autoDetectProxy ?? true,
    drilldown: params.drilldown ?? null,
    combine: null
  };
  return {
    request_id: params.requestId ?? null,
    chains: [
      {
        chain_id: params.chainId,
        block: blockToBlockSpec(params.block),
        contracts: [target]
      }
    ],
    drilldown_defaults: null,
    combine: null,
    budget: params.budget,
    include_source_code: params.includeSourceCode ?? false
  };
}

// src/model/abi.ts
var abi_exports = {};
__reExport(abi_exports, abi_star);

// src/provider/source.ts
var log = createLogger(`${CONTRACT_EXPLORER_PACKAGE}:provider`);
function describeAbiFailures(failures) {
  const message = failures.map((f) => f.error instanceof Error ? f.error.message : String(f.error ?? "")).find((m) => m.trim().length > 0);
  if (message) return message;
  const names = [...new Set(failures.map((f) => f.provider))].join(", ");
  return `The ABI lookup failed (${names || "unknown source"}).`;
}
function sourceEnabled(source, options) {
  switch (source) {
    case AbiSourceKind.CATALOG:
      return options.useCatalog;
    case AbiSourceKind.DEFAULTS:
      return options.useDefaults;
    case AbiSourceKind.EXTERNAL:
      return options.useExternalSources;
    default:
      return true;
  }
}
var AbiSourcesUnavailableError = class extends Error {
  constructor(failures) {
    const names = [...new Set(failures.map((f) => f.provider))].join(", ");
    super(`No ABI source could be reached (${names}). This is not the same as the contract having no ABI.`);
    this.failures = failures;
    this.name = "AbiSourcesUnavailableError";
  }
  failures;
};
function createCompositeAbiProvider(providers) {
  return {
    id: "composite",
    sources: [AbiSourceKind.CATALOG, AbiSourceKind.DEFAULTS, AbiSourceKind.EXTERNAL],
    async fetch(ref, options, ctx) {
      const failures = [];
      for (const source of options.sourcePriority) {
        if (!sourceEnabled(source, options)) continue;
        for (const provider of providers.filter((p) => p.sources.includes(source))) {
          try {
            const result = await provider.fetch(ref, options, ctx);
            if (result && result.abi.entries.length > 0) {
              return {
                ...result,
                origin: result.origin || source,
                ...failures.length > 0 ? { failures: [...failures] } : {}
              };
            }
          } catch (err) {
            log.warn("provider fetch failed", { provider: provider.id, source, error: String(err) });
            failures.push({ provider: provider.id, source, error: err });
          }
        }
      }
      if (failures.length > 0) throw new AbiSourcesUnavailableError(failures);
      return null;
    }
  };
}
function createInMemoryAbiProvider(seeds, config = {}) {
  const id = config.id ?? "in-memory";
  const source = config.source ?? AbiSourceKind.CATALOG;
  const index = /* @__PURE__ */ new Map();
  for (const seed of seeds) index.set(`${seed.chainId.toLowerCase()}:${seed.address.toLowerCase()}`, seed);
  return {
    id,
    sources: [source],
    async fetch(ref) {
      const seed = index.get(`${ref.chainId.toLowerCase()}:${ref.address.toLowerCase()}`);
      if (!seed) return null;
      if (seed.dto) {
        return { abi: (0, abi_exports.normalizedAbiFromDto)(seed.dto), meta: (0, abi_exports.abiMetaFromDto)(seed.dto), origin: id };
      }
      return {
        abi: (0, abi_exports.normalizeAbi)(seed.entries ?? []),
        meta: { chainId: ref.chainId, address: ref.address, ...seed.meta },
        origin: id
      };
    }
  };
}
function createDefaultAbiProvider() {
  return {
    id: "defaults",
    sources: [AbiSourceKind.DEFAULTS],
    async fetch(ref) {
      return {
        abi: (0, abi_exports.defaultErcAbi)(),
        meta: {
          chainId: ref.chainId,
          address: ref.address,
          source: "defaults",
          // A fixed floor, not a measurement. It exists to rank this BELOW every
          // real source; there is nothing about this contract being scored, and
          // the number rendered bare — "confidence 20%" beside no explanation —
          // read as a judgement about USDT rather than about the fallback.
          confidence: 0.2,
          explanation: [
            "No stored, catalogued or verified ABI was found for this address.",
            "Showing the well-known ERC-20 and ERC-721 interfaces as a fallback.",
            "Functions listed here are the standard ones, not this contract's \u2014 some may not exist on it."
          ]
        },
        origin: AbiSourceKind.DEFAULTS,
        isGuess: true
      };
    }
  };
}
var log2 = createLogger(`${CONTRACT_EXPLORER_PACKAGE}:api-abi`);
function abiContractPath(base, ref) {
  return `${base}/${encodeURIComponent(ref.chainId)}/${encodeURIComponent(ref.address)}`;
}
function metaFromWire(wire) {
  return {
    chainId: wire.chain_id,
    address: wire.address,
    contractName: wire.name ?? null,
    source: wire.source ?? null,
    updatedAt: wire.updated_at ?? null,
    orgId: wire.owner?.org_id ?? null,
    tenantId: wire.owner?.tenant_id ?? null,
    abiId: wire.abi_id,
    scope: wire.scope ?? null,
    isDefault: Boolean(wire.is_default),
    inherited: Boolean(wire.inherited),
    confidence: wire.confidence ?? void 0,
    lifecycle: wire.lifecycle ?? void 0,
    explanation: wire.explanation ?? null,
    evidence: wire.evidence ?? null,
    validFromBlock: wire.valid_from_block ?? null,
    validToBlock: wire.valid_to_block ?? null
  };
}
function blockQuery(ref) {
  const height = ref.block?.blockNumber;
  return height == null ? void 0 : { block: height.toString() };
}
function createApiAbiProvider(client, options = {}) {
  const base = options.basePath ?? "/abis";
  const id = options.id ?? "abi-mgmt-api";
  return {
    id,
    sources: [AbiSourceKind.CATALOG],
    async fetch(ref, _options, ctx) {
      let wire;
      try {
        wire = unwrapData(
          await client.get(abiContractPath(base, ref), {
            query: blockQuery(ref),
            signal: ctx?.signal
          })
        );
      } catch (err) {
        if (err instanceof ApiError && err.statusCode === 404) {
          if (err.details?.reason === "lookup_failed") throw err;
          return null;
        }
        throw err;
      }
      const entries = wire?.entries ?? [];
      if (entries.length === 0) return null;
      return { abi: (0, abi_exports.normalizeAbi)(entries), meta: metaFromWire(wire), origin: id };
    }
  };
}
function withBlockRange(body, opts) {
  if (opts?.validFromBlock != null) body.valid_from_block = opts.validFromBlock;
  if (opts?.validToBlock != null) body.valid_to_block = opts.validToBlock;
  return body;
}
function abiSourceId(entry) {
  if (entry.origin === "catalog") return "catalog";
  return entry.abiId ?? `scope:${entry.scope ?? ""}`;
}
function abiSourceScopeLabel(entry) {
  if (entry.origin === "catalog") return "Catalog";
  if (entry.isDefault) return "System default";
  const rung = scopeRungLabel(entry.scope);
  if (entry.inherited) return rung ? `Inherited \xB7 ${rung}` : "Inherited";
  return rung ? `Your ${rung.toLowerCase()} scope` : "Your workspace";
}
function entriesOf(wire) {
  return Array.isArray(wire.entries) ? wire.entries : void 0;
}
function overrideToEntry(w) {
  return {
    origin: "override",
    abiId: w.abi_id,
    chainId: w.chain_id,
    address: w.address,
    name: w.name ?? null,
    source: w.source ?? null,
    status: w.status ?? null,
    scope: w.scope ?? null,
    isDefault: Boolean(w.is_default),
    inherited: Boolean(w.inherited),
    entriesCount: typeof w.entries_count === "number" ? w.entries_count : entriesOf(w)?.length ?? 0,
    entries: entriesOf(w),
    updatedAt: w.updated_at ?? null,
    validFromBlock: w.valid_from_block ?? null,
    validToBlock: w.valid_to_block ?? null
  };
}
function createApiAbiByAddressClient(client, options = {}) {
  const base = options.basePath ?? "/abis";
  return async (ref, opts) => {
    const path = `${base}/by-address/${encodeURIComponent(ref.chainId)}/${encodeURIComponent(ref.address)}`;
    const query = {};
    if (opts?.includeEntries !== false) query.include = "entries";
    if (opts?.block != null) query.block = opts.block.toString();
    const wire = unwrapData(
      await client.get(path, {
        query: Object.keys(query).length > 0 ? query : void 0,
        signal: opts?.signal
      })
    );
    const sources = (wire?.overrides ?? []).map(overrideToEntry);
    if (wire?.catalog) {
      const c = wire.catalog;
      sources.push({
        origin: "catalog",
        chainId: c.chain_id ?? ref.chainId,
        address: c.address ?? ref.address,
        name: c.name ?? c.contract_name ?? null,
        source: c.source ?? null,
        scope: null,
        isDefault: false,
        inherited: true,
        isProxy: Boolean(c.is_proxy),
        entriesCount: typeof c.entries_count === "number" ? c.entries_count : entriesOf(c)?.length ?? 0,
        entries: entriesOf(c),
        updatedAt: c.updated_at ?? null,
        validFromBlock: c.valid_from_block ?? null,
        validToBlock: c.valid_to_block ?? null
      });
    }
    return {
      chainId: wire?.chain_id ?? ref.chainId,
      address: wire?.address ?? ref.address,
      block: wire?.block ?? null,
      sources
    };
  };
}
function createApiAbiWriter(client, options = {}) {
  const base = options.basePath ?? "/abis";
  const canEdit = Boolean(options.canEdit);
  const canCreate = options.canCreate === void 0 ? canEdit : Boolean(options.canCreate);
  const scopeQuery = (opts) => opts?.scope ? { scope: opts.scope } : void 0;
  const create = async (ref, entries, opts) => {
    const body = { chain_id: ref.chainId, address: ref.address, entries };
    if (opts?.name !== void 0) body.name = opts.name;
    if (opts?.description !== void 0) body.description = opts.description;
    withBlockRange(body, opts);
    const created = unwrapData(
      await client.post(base, body, { query: scopeQuery(opts), signal: opts?.signal })
    );
    const replaced = created?.replaced;
    return {
      imported: false,
      replaced: typeof replaced === "boolean" ? replaced : void 0,
      meta: metaFromWire(created)
    };
  };
  return {
    canEdit,
    canCreate,
    create,
    async save(ref, entries, opts) {
      const body = { entries };
      if (opts?.name !== void 0) body.name = opts.name;
      if (opts?.description !== void 0) body.description = opts.description;
      withBlockRange(body, opts);
      try {
        const wire = unwrapData(
          await client.patch(abiContractPath(base, ref), body, {
            query: scopeQuery(opts),
            signal: opts?.signal
          })
        );
        return { imported: Boolean(wire?.imported), meta: metaFromWire(wire) };
      } catch (err) {
        if (err instanceof ApiError && err.statusCode === 404) {
          if (!canCreate) {
            throw new Error(
              "No ABI exists for this contract yet, and registering a new one needs the abis:create permission."
            );
          }
          return create(ref, entries, opts);
        }
        throw err;
      }
    },
    async refresh(ref, opts) {
      const wire = unwrapData(
        await client.post(`${abiContractPath(base, ref)}/refresh`, void 0, {
          signal: opts?.signal
        })
      );
      return {
        refreshed: wire?.refreshed !== false,
        found: Boolean(wire?.found),
        // Deliberately not defaulted to false: the miss branch omits the key
        // entirely, and "we didn't say" is not "we didn't save it".
        persisted: typeof wire?.persisted === "boolean" ? wire.persisted : void 0,
        providersQueried: wire?.providers_queried ?? null,
        contractName: wire?.contract_name ?? null,
        entriesCount: wire?.entries_count ?? null
      };
    },
    async delete(ref, opts) {
      unwrapData(
        await client.delete(abiContractPath(base, ref), {
          query: opts?.block != null ? { block: opts.block.toString() } : void 0,
          signal: opts?.signal
        })
      );
    }
  };
}
function proxyKindFromPattern(pattern) {
  switch ((pattern ?? "").toLowerCase()) {
    case "eip1967":
      return ProxyKind.EIP1967;
    case "uups":
    case "eip1822":
      return ProxyKind.UUPS;
    case "beacon":
      return ProxyKind.BEACON;
    case "transparent":
      return ProxyKind.TRANSPARENT;
    case "diamond":
    case "eip2535":
      return ProxyKind.DIAMOND;
    case "none":
    case "":
      return ProxyKind.UNKNOWN;
    default:
      return ProxyKind.CUSTOM;
  }
}
function createApiProxyResolver(client, options = {}) {
  const base = options.basePath ?? "/proxy-overrides";
  return async (ref, opts) => {
    const root = ref.address;
    let wire;
    try {
      wire = unwrapData(
        await client.get(abiContractPath(base, ref), {
          query: blockQuery(ref),
          signal: opts?.signal
        })
      );
    } catch (err) {
      log2.warn("proxy resolve failed", { chain: ref.chainId, address: ref.address, error: String(err) });
      throw err;
    }
    const explanation = (wire?.explanation ?? []).filter(Boolean);
    const probeFailed = (wire?.evidence ?? []).some((e) => (e?.status ?? "").toLowerCase() === "failed");
    if (!wire?.is_proxy) {
      const chain = directChain(root);
      return {
        ...chain,
        ...probeFailed ? { truncated: "probe-failed" } : {},
        ...explanation.length > 0 ? { explanation } : {},
        ...wire?.resolved_by ? { resolvedBy: wire.resolved_by } : {}
      };
    }
    const impls = wire.implementations ?? [];
    const hops = impls.map((impl) => ({
      address: impl.address,
      kind: proxyKindFromPattern(impl.pattern ?? wire?.pattern)
    }));
    const effectiveAddress = hops[hops.length - 1]?.address ?? root;
    const beaconUnresolved = hops.length > 0 && hops[hops.length - 1]?.kind === ProxyKind.BEACON;
    const truncated = beaconUnresolved ? "beacon-unresolved" : probeFailed ? "probe-failed" : void 0;
    return {
      root,
      hops,
      effectiveAddress,
      isProxy: hops.length > 0,
      ...truncated ? { truncated } : {},
      ...explanation.length > 0 ? { explanation } : {},
      ...wire.resolved_by ? { resolvedBy: wire.resolved_by } : {}
    };
  };
}
function sourceFromWire(w, ref) {
  const out = {
    chainId: w.chain_id ?? ref.chainId,
    address: w.address ?? ref.address,
    origin: w.origin ?? "third_party",
    found: Boolean(w.found),
    verified: w.verified,
    sourceAvailable: w.source_available,
    contractName: w.contract_name ?? null,
    compilerVersion: w.compiler_version ?? null,
    optimizationUsed: w.optimization_used ?? null,
    runs: w.runs ?? null,
    license: w.license ?? null,
    language: w.language ?? null,
    isProxy: w.is_proxy,
    implementationAddresses: w.implementation_addresses,
    sourceCode: w.source_code ?? null,
    abiEntriesCount: w.abi_entries_count,
    confidence: w.confidence ?? null,
    providersQueried: w.providers_queried ?? null,
    contributors: w.contributors ?? null
  };
  if (Array.isArray(w.abi)) out.abi = w.abi;
  if (w.bytecode) {
    out.bytecode = {
      present: w.bytecode.present ?? null,
      bytecode: w.bytecode.bytecode ?? null,
      sizeBytes: w.bytecode.size_bytes,
      block: w.bytecode.block ?? null,
      error: w.bytecode.error
    };
  }
  return out;
}
function createApiContractSourceClient(client, options = {}) {
  const base = options.basePath ?? "/source";
  return async (ref, opts) => {
    const include = [];
    if (opts?.includeBytecode) include.push("bytecode");
    if (opts?.includeAbi) include.push("abi");
    const query = {};
    if (include.length) query.include = include.join(",");
    if (opts?.providers?.length) query.providers = opts.providers.join(",");
    if (opts?.skipCache) query.skip_cache = "true";
    if (opts?.block != null) query.block = String(opts.block);
    const path = `${base}/${encodeURIComponent(ref.chainId)}/${encodeURIComponent(ref.address)}`;
    const wire = unwrapData(
      await client.get(path, {
        query: Object.keys(query).length ? query : void 0,
        signal: opts?.signal
      })
    );
    return sourceFromWire(wire ?? {}, ref);
  };
}

// src/model/contract.ts
var contract_exports = {};
__reExport(contract_exports, contracts_star);

// src/provider/cache.ts
function identityKey(identity) {
  if (!identity) return "app";
  return [
    identity.app ? `app=${identity.app}` : "",
    identity.customerId ? `cust=${identity.customerId}` : "",
    identity.orgId ? `org=${identity.orgId}` : "",
    identity.tenantId ? `tenant=${identity.tenantId}` : "",
    identity.userId ? `user=${identity.userId}` : ""
  ].filter(Boolean).join("&");
}
function abiCacheKey(ref, options, identity) {
  return `${identityKey(identity)}::${(0, contract_exports.contractRefKey)(ref)}::${optionsKey(options)}`;
}
var AbiCache = class {
  constructor(max = ABI_CACHE_MAX_ENTRIES, ttlMs = ABI_CACHE_TTL_MS) {
    this.max = max;
    this.ttlMs = ttlMs;
  }
  max;
  ttlMs;
  map = /* @__PURE__ */ new Map();
  get(key) {
    const entry = this.map.get(key);
    if (!entry) return void 0;
    if (Date.now() - entry.at > this.ttlMs) {
      this.map.delete(key);
      return void 0;
    }
    this.map.delete(key);
    this.map.set(key, entry);
    return entry.result;
  }
  set(key, result) {
    this.map.delete(key);
    this.map.set(key, { result, at: Date.now() });
    while (this.map.size > this.max) {
      const oldest = this.map.keys().next().value;
      if (oldest === void 0) break;
      this.map.delete(oldest);
    }
  }
  delete(key) {
    this.map.delete(key);
  }
  /** Drop every entry for a contract ref (all option/identity variants). */
  invalidateRef(ref) {
    const needle = (0, contract_exports.contractRefKey)(ref);
    for (const key of [...this.map.keys()]) {
      if (key.includes(needle)) this.map.delete(key);
    }
  }
  clear() {
    this.map.clear();
  }
};
var sharedAbiCache = new AbiCache();

// src/provider/validatingClient.ts
function createValidatingClient(schema, options) {
  const { fetcher, id = "backend", validate = true } = options;
  return {
    id,
    async read(request, opts) {
      const raw = await fetcher(request, opts);
      return validate ? schema.parse(raw) : raw;
    }
  };
}
function createContractStateClient(options) {
  return createValidatingClient(
    ContractStateResultSchema,
    options
  );
}
function createApiContractStateClient(client, options = {}) {
  const { path = "/abis/contract-state", validate, id = "abi-mgmt-api" } = options;
  return createContractStateClient({
    id,
    validate,
    fetcher: async (request, opts) => unwrapData(
      await client.post(path, request, { signal: opts?.signal })
    )
  });
}
function createTransactionInsightClient(options) {
  return createValidatingClient(
    TransactionStateInsightSchema,
    options
  );
}
function createApiTransactionInsightClient(client, options = {}) {
  const { path = "/abis/transaction-insight", validate, id = "abi-mgmt-api" } = options;
  return createTransactionInsightClient({
    id,
    validate,
    fetcher: async (request, opts) => unwrapData(
      await client.post(path, request, {
        signal: opts?.signal
      })
    )
  });
}
var ReaderContext = React19.createContext(null);
var fallbackReader = null;
function defaultContractReader() {
  fallbackReader ??= createRpcContractReader();
  return fallbackReader;
}
function ContractReaderProvider({ reader, children }) {
  const value = reader ?? defaultContractReader();
  return /* @__PURE__ */ jsx(ReaderContext.Provider, { value, children });
}
function useContractReader(override) {
  const ctx = React19.useContext(ReaderContext);
  return override ?? ctx ?? defaultContractReader();
}
var ContractStateClientContext = React19.createContext(null);
function ContractStateClientProvider({
  client,
  children
}) {
  return /* @__PURE__ */ jsx(ContractStateClientContext.Provider, { value: client, children });
}
function useContractStateClient(override) {
  const ctx = React19.useContext(ContractStateClientContext);
  return override ?? ctx ?? null;
}
var TransactionInsightClientContext = React19.createContext(null);
function TransactionInsightClientProvider({
  client,
  children
}) {
  return /* @__PURE__ */ jsx(TransactionInsightClientContext.Provider, { value: client, children });
}
function useTransactionInsightClient(override) {
  const ctx = React19.useContext(TransactionInsightClientContext);
  return override ?? ctx ?? null;
}
function stableArgs(args) {
  if (!args || args.length === 0) return "";
  return JSON.stringify(args, (_k, v) => typeof v === "bigint" ? `${v.toString()}n` : v);
}
function useReaderRead(params) {
  const { reader, ref, abi, functionName, args, enabled = true } = params;
  const [data, setData] = React19.useState(void 0);
  const [error, setError] = React19.useState(null);
  const [isPending, setIsPending] = React19.useState(false);
  const [nonce, setNonce] = React19.useState(0);
  const ready = enabled && args !== null;
  const refKey = (0, contract_exports.contractRefKey)(ref);
  const argsKey = stableArgs(args);
  if (useDepsChanged([refKey, functionName, argsKey, ready])) {
    setData(void 0);
    setError(null);
  }
  React19.useEffect(() => {
    if (!ready) {
      setData(void 0);
      setError(null);
      setIsPending(false);
      return void 0;
    }
    const controller = new AbortController();
    setIsPending(true);
    setError(null);
    reader.read(ref, { abi: abi.entries, functionName, args: args ?? void 0 }, { signal: controller.signal }).then((result) => {
      if (controller.signal.aborted) return;
      setData(result);
      setIsPending(false);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsPending(false);
    });
    return () => controller.abort();
  }, [reader, ready, refKey, functionName, argsKey, nonce]);
  const refetch = React19.useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, isPending, refetch };
}
function requestKey(request) {
  if (request === null || request === void 0) return "";
  return JSON.stringify(request, (_k, v) => typeof v === "bigint" ? `${v.toString()}n` : v);
}
function useClientRead(params) {
  const { client, request, enabled = true } = params;
  const [data, setData] = React19.useState(null);
  const [error, setError] = React19.useState(null);
  const [isPending, setIsPending] = React19.useState(false);
  const [nonce, setNonce] = React19.useState(0);
  const ready = enabled && request !== null && client !== null;
  const key = requestKey(request);
  if (useDepsChanged([key, ready])) {
    setData(null);
    setError(null);
  }
  React19.useEffect(() => {
    if (!ready || !client || request === null) {
      setData(null);
      setError(null);
      setIsPending(false);
      return void 0;
    }
    const controller = new AbortController();
    setIsPending(true);
    setError(null);
    client.read(request, { signal: controller.signal }).then((res) => {
      if (controller.signal.aborted) return;
      setData(res);
      setIsPending(false);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsPending(false);
    });
    return () => controller.abort();
  }, [client, ready, key, nonce]);
  const refetch = React19.useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, isPending, unwired: client === null, refetch };
}

// src/data/useContractState.ts
function useContractState(params) {
  const { request, client: override, enabled = true } = params;
  const client = useContractStateClient(override);
  const { data, ...rest } = useClientRead({
    client,
    request,
    enabled
  });
  return { result: data, ...rest };
}

// src/data/useTransactionInsight.ts
function useTransactionInsight(params) {
  const { request, client: override, enabled = true } = params;
  const client = useTransactionInsightClient(override);
  const { data, ...rest } = useClientRead({ client, request, enabled });
  return { insight: data, ...rest };
}
function useAbiStorage(identity) {
  const key = JSON.stringify(identity ?? {});
  return React19.useMemo(() => new ScopedStorage(identity ?? {}), [key]);
}
function useAbiOptions(identity, initial) {
  const storage = useAbiStorage(identity);
  const initialRef = React19.useRef(initial);
  initialRef.current = initial;
  const load = React19.useCallback(
    () => ({
      ...DEFAULT_ABI_FETCH_OPTIONS,
      ...initialRef.current,
      ...storage.get(ABI_OPTIONS_NAME, {})
    }),
    [storage]
  );
  const [options, setState] = React19.useState(load);
  React19.useEffect(() => {
    setState(load());
  }, [load]);
  const setOptions = React19.useCallback(
    (next) => {
      setState((prev) => {
        const merged = { ...prev, ...next };
        storage.set(ABI_OPTIONS_NAME, merged);
        return merged;
      });
    },
    [storage]
  );
  const reset = React19.useCallback(() => {
    storage.remove(ABI_OPTIONS_NAME);
    setState({ ...DEFAULT_ABI_FETCH_OPTIONS, ...initialRef.current });
  }, [storage]);
  return { options, setOptions, reset };
}
function useInlineAbis(identity) {
  const storage = useAbiStorage(identity);
  const [all, setAll] = React19.useState(() => storage.get(INLINE_ABIS_NAME, {}));
  React19.useEffect(() => {
    setAll(storage.get(INLINE_ABIS_NAME, {}));
  }, [storage]);
  const persist = React19.useCallback(
    (next) => {
      storage.set(INLINE_ABIS_NAME, next);
      setAll(next);
    },
    [storage]
  );
  const get = React19.useCallback(
    (ref) => all[(0, contract_exports.addressKey)(ref)]?.entries ?? null,
    [all]
  );
  const set = React19.useCallback(
    (ref, entries) => {
      persist({ ...all, [(0, contract_exports.addressKey)(ref)]: { entries, updatedAt: Date.now() } });
    },
    [all, persist]
  );
  const remove = React19.useCallback(
    (ref) => {
      const next = { ...all };
      delete next[(0, contract_exports.addressKey)(ref)];
      persist(next);
    },
    [all, persist]
  );
  const clearAll = React19.useCallback(() => persist({}), [persist]);
  return { all, get, set, remove, clearAll };
}
function toRecord(p) {
  return {
    id: p.id,
    label: p.label,
    chainId: p.ref.chainId,
    address: p.ref.address,
    block: (0, contract_exports.blockToJson)(p.ref.block),
    entries: p.entries,
    meta: p.meta
  };
}
function fromRecord(r) {
  return {
    id: r.id,
    label: r.label,
    ref: { chainId: r.chainId, address: r.address, block: (0, contract_exports.blockFromJson)(r.block), label: r.label },
    entries: r.entries,
    meta: r.meta
  };
}
function usePinnedAbis(identity) {
  const storage = useAbiStorage(identity);
  const [records, setRecords] = React19.useState(
    () => storage.get(PINNED_ABIS_NAME, [])
  );
  React19.useEffect(() => {
    setRecords(storage.get(PINNED_ABIS_NAME, []));
  }, [storage]);
  const persist = React19.useCallback(
    (next) => {
      const capped = next.slice(-PINNED_ABIS_LIMIT);
      storage.set(PINNED_ABIS_NAME, capped);
      setRecords(capped);
    },
    [storage]
  );
  const pin = React19.useCallback(
    (entry) => {
      const id = entry.id ?? `${entry.ref.chainId}:${entry.ref.address}:${Date.now()}`;
      const record = toRecord({ ...entry, id });
      const next = [...records.filter((r) => r.id !== id), record];
      persist(next);
      return id;
    },
    [records, persist]
  );
  const unpin = React19.useCallback(
    (id) => persist(records.filter((r) => r.id !== id)),
    [records, persist]
  );
  const clear = React19.useCallback(() => persist([]), [persist]);
  const isPinned = React19.useCallback(
    (chainId, address) => records.some((r) => r.chainId.toLowerCase() === chainId.toLowerCase() && r.address.toLowerCase() === address.toLowerCase()),
    [records]
  );
  const pinned = React19.useMemo(() => records.map(fromRecord), [records]);
  return { pinned, pin, unpin, clear, isPinned };
}
var log3 = createLogger(`${CONTRACT_EXPLORER_PACKAGE}:resolver`);
function useAbiResolver(params) {
  const { ref, provider, identity, inlineEntries, enabled = true } = params;
  const options = params.options ?? DEFAULT_ABI_FETCH_OPTIONS;
  const cache = params.cache ?? sharedAbiCache;
  const [resolved, setResolved] = React19.useState(null);
  const [status, setStatus] = React19.useState("idle");
  const [error, setError] = React19.useState(null);
  const [nonce, setNonce] = React19.useState(0);
  const refKey = ref ? (0, contract_exports.contractRefKey)(ref) : "";
  const optsKey = optionsKey(options);
  const idKey = identityKey(identity);
  const inlineKey = inlineEntries && inlineEntries.length > 0 ? JSON.stringify(inlineEntries) : "";
  if (useDepsChanged([refKey, optsKey, idKey, inlineKey, enabled])) {
    setResolved(null);
    setError(null);
    setStatus(enabled && ref ? "loading" : "idle");
  }
  React19.useEffect(() => {
    if (!enabled || !ref) {
      setResolved(null);
      setStatus("idle");
      setError(null);
      return void 0;
    }
    if (inlineEntries && inlineEntries.length > 0) {
      setResolved({
        ref,
        abi: (0, abi_exports.normalizeAbi)(inlineEntries),
        meta: { chainId: ref.chainId, address: ref.address, source: "inline" },
        origin: "inline"
      });
      setStatus("ready");
      setError(null);
      return void 0;
    }
    const key = abiCacheKey(ref, options, identity);
    const cached = cache.get(key);
    if (cached) {
      setResolved({
        ref,
        abi: cached.abi,
        meta: cached.meta,
        origin: cached.origin || "cache",
        isGuess: cached.isGuess,
        failures: cached.failures
      });
      setStatus("ready");
      setError(null);
      return void 0;
    }
    if (!provider) {
      setResolved(null);
      setStatus("empty");
      return void 0;
    }
    const controller = new AbortController();
    setStatus("loading");
    setError(null);
    provider.fetch(ref, options, { identity, signal: controller.signal }).then((result) => {
      if (controller.signal.aborted) return;
      if (!result || result.abi.entries.length === 0) {
        setResolved(null);
        setStatus("empty");
        return;
      }
      cache.set(key, result);
      setResolved({
        ref,
        abi: result.abi,
        meta: result.meta,
        origin: result.origin,
        isGuess: result.isGuess,
        failures: result.failures
      });
      setStatus("ready");
    }).catch((err) => {
      if (controller.signal.aborted) return;
      log3.warn("abi resolve failed", { ref: refKey, error: String(err) });
      setError(err instanceof Error ? err : new Error(String(err)));
      setStatus("error");
    });
    return () => controller.abort();
  }, [enabled, refKey, optsKey, idKey, inlineKey, provider, cache, nonce]);
  const refetch = React19.useCallback(() => {
    if (ref) cache.invalidateRef(ref);
    setNonce((n) => n + 1);
  }, [cache, ref]);
  return { resolved, status, error, origin: resolved?.origin ?? null, refetch };
}
var log4 = createLogger(`${CONTRACT_EXPLORER_PACKAGE}:abi-sources`);
function useAbiSources(ref, client, opts = true) {
  const { enabled = true, block = null } = typeof opts === "boolean" ? { enabled: opts } : opts;
  const [sources, setSources] = React19.useState([]);
  const [isLoading, setIsLoading] = React19.useState(false);
  const [error, setError] = React19.useState(null);
  const [nonce, setNonce] = React19.useState(0);
  const blockKey4 = block == null ? "" : block.toString();
  const key = ref ? `${ref.chainId.toLowerCase()}:${ref.address.toLowerCase()}@${blockKey4}` : "";
  React19.useEffect(() => {
    if (!enabled || !client || !ref) {
      setSources([]);
      setError(null);
      setIsLoading(false);
      return void 0;
    }
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    client(ref, { includeEntries: true, block, signal: controller.signal }).then((res) => {
      if (controller.signal.aborted) return;
      setSources(res.sources);
      setIsLoading(false);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      log4.warn("abi sources fetch failed", { ref: key, error: String(err) });
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsLoading(false);
    });
    return () => controller.abort();
  }, [enabled, client, key, nonce]);
  const refetch = React19.useCallback(() => setNonce((n) => n + 1), []);
  return { sources, isLoading, error, refetch };
}
var log5 = createLogger(`${CONTRACT_EXPLORER_PACKAGE}:proxy`);
function useProxyChain(ref, opts = {}) {
  const { discover = true, enabled = true, maxHops, resolver } = opts;
  const reader = useContractReader(opts.reader);
  const [chain, setChain] = React19.useState(null);
  const [isResolving, setIsResolving] = React19.useState(false);
  const [error, setError] = React19.useState(null);
  const [nonce, setNonce] = React19.useState(0);
  const refKey = ref ? (0, contract_exports.contractRefKey)(ref) : "";
  if (useDepsChanged([refKey, enabled, discover, maxHops])) {
    setChain(null);
    setError(null);
  }
  React19.useEffect(() => {
    if (!enabled || !ref) {
      setChain(null);
      setError(null);
      setIsResolving(false);
      return void 0;
    }
    if (!discover) {
      setChain((0, contract_exports.directChain)(ref.address));
      setIsResolving(false);
      return void 0;
    }
    let cancelled = false;
    let abort;
    setIsResolving(true);
    setError(null);
    let work;
    if (resolver) {
      const controller = new AbortController();
      abort = () => controller.abort();
      work = resolver(ref, { signal: controller.signal });
    } else {
      const deps = (0, contract_exports.readerProxyDeps)(reader, ref, maxHops);
      if (!deps) {
        setChain((0, contract_exports.directChain)(ref.address));
        setIsResolving(false);
        return void 0;
      }
      work = (0, contract_exports.resolveProxyChain)(ref.address, deps);
    }
    work.then((result) => {
      if (!cancelled) {
        setChain(result);
        setIsResolving(false);
      }
    }).catch((err) => {
      if (cancelled) return;
      log5.warn("proxy resolve failed", { ref: refKey, error: String(err) });
      setChain(null);
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsResolving(false);
    });
    return () => {
      cancelled = true;
      abort?.();
    };
  }, [enabled, discover, reader, resolver, refKey, maxHops, nonce]);
  const refetch = React19.useCallback(() => setNonce((n) => n + 1), []);
  return { chain, isResolving, error, refetch };
}
function useContractFields(ref, abi, opts = {}) {
  const enabled = opts.enabled ?? true;
  const reader = useContractReader(opts.reader);
  const noArgReads = React19.useMemo(
    () => abi ? abi.reads.filter((fn) => (fn.inputs ?? []).length === 0) : [],
    [abi]
  );
  const needArguments = React19.useMemo(
    () => abi ? abi.reads.filter((fn) => (fn.inputs ?? []).length > 0) : [],
    [abi]
  );
  const [results, setResults] = React19.useState([]);
  const [isPending, setIsPending] = React19.useState(false);
  const [error, setError] = React19.useState(null);
  const [nonce, setNonce] = React19.useState(0);
  const ready = enabled && Boolean(ref && abi) && noArgReads.length > 0;
  const refKey = ref ? (0, contract_exports.contractRefKey)(ref) : "";
  const fieldsKey = noArgReads.map((fn) => `${fn.name}:${(fn.outputs ?? []).map((o) => o.type).join(",")}`).join("|");
  if (useDepsChanged([refKey, fieldsKey, ready])) {
    setResults([]);
    setError(null);
  }
  React19.useEffect(() => {
    if (!ready || !ref || !abi) {
      setResults([]);
      setIsPending(false);
      setError(null);
      return void 0;
    }
    const controller = new AbortController();
    setIsPending(true);
    setError(null);
    const reqs = noArgReads.map((fn) => ({
      abi: abi.entries,
      functionName: fn.name,
      args: []
    }));
    reader.readMany(ref, reqs, { signal: controller.signal }).then((outcomes) => {
      if (controller.signal.aborted) return;
      setResults(outcomes);
      setIsPending(false);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      setResults([]);
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsPending(false);
    });
    return () => controller.abort();
  }, [reader, ready, refKey, fieldsKey, nonce]);
  const fields = React19.useMemo(
    () => noArgReads.map((fn, i) => ({ fn, outcome: results[i] })),
    [noArgReads, results]
  );
  const refetch = React19.useCallback(() => setNonce((n) => n + 1), []);
  return { fields, needArguments, isPending, error, refetch };
}
var IDENTITY_ABI = parseAbi([
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)"
]);
var EMPTY = { name: null, symbol: null, decimals: null, resolved: false };
function asText(value) {
  if (typeof value !== "string") return null;
  const cleaned = value.replaceAll("\0", "").trim();
  return cleaned.length > 0 ? cleaned : null;
}
function useTokenIdentity(ref, opts = {}) {
  const enabled = opts.enabled ?? true;
  const reader = useContractReader(opts.reader);
  const [identity, setIdentity] = React19.useState(EMPTY);
  const key = ref ? (0, contract_exports.contractRefKey)({ chainId: ref.chainId, address: ref.address }) : "";
  React19.useEffect(() => {
    if (!enabled || !ref) {
      setIdentity(EMPTY);
      return void 0;
    }
    let cancelled = false;
    setIdentity(EMPTY);
    reader.readMany({ chainId: ref.chainId, address: ref.address }, [
      { abi: IDENTITY_ABI, functionName: "name", args: [] },
      { abi: IDENTITY_ABI, functionName: "symbol", args: [] },
      { abi: IDENTITY_ABI, functionName: "decimals", args: [] }
    ]).then((outcomes) => {
      if (cancelled) return;
      const [name, symbol, decimals] = outcomes;
      setIdentity({
        name: name?.status === "success" ? asText(name.data) : null,
        symbol: symbol?.status === "success" ? asText(symbol.data) : null,
        decimals: decimals?.status === "success" && typeof decimals.data === "number" ? decimals.data : null,
        resolved: true
      });
    }).catch(() => {
      if (!cancelled) setIdentity({ ...EMPTY, resolved: true });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, key, reader]);
  return identity;
}

// src/data/eventLogCache.ts
function boundKey(value) {
  if (value == null) return "*";
  return typeof value === "bigint" ? `#${value.toString()}` : value;
}
function eventLogCacheKey(readerId, ref, query = {}) {
  const range = `${boundKey(query.fromBlock)}..${boundKey(query.toBlock)}`;
  const topics = query.topics && query.topics.length ? query.topics.join(",") : "*";
  return `${readerId}::${(0, contract_exports.contractRefKey)(ref)}::${range}::${topics}`;
}
var EventLogCache = class {
  constructor(max = EVENT_LOG_CACHE_MAX_ENTRIES, ttlMs = EVENT_LOG_CACHE_TTL_MS) {
    this.max = max;
    this.ttlMs = ttlMs;
  }
  max;
  ttlMs;
  map = /* @__PURE__ */ new Map();
  get(key) {
    const entry = this.map.get(key);
    if (!entry) return void 0;
    if (Date.now() - entry.at > this.ttlMs) {
      this.map.delete(key);
      return void 0;
    }
    this.map.delete(key);
    this.map.set(key, entry);
    return entry.logs;
  }
  set(key, logs) {
    this.map.delete(key);
    this.map.set(key, { logs, at: Date.now() });
    while (this.map.size > this.max) {
      const oldest = this.map.keys().next().value;
      if (oldest === void 0) break;
      this.map.delete(oldest);
    }
  }
  /** Drop every cached range for a contract ref (all reader/range/topic variants). */
  invalidateRef(ref) {
    const needle = (0, contract_exports.contractRefKey)(ref);
    for (const key of [...this.map.keys()]) {
      if (key.includes(needle)) this.map.delete(key);
    }
  }
  clear() {
    this.map.clear();
  }
};
var sharedEventLogCache = new EventLogCache();

// src/data/useEventLogs.ts
var EMPTY_ABI = (0, abi_exports.normalizeAbi)([]);
function queryKey(query) {
  if (!query) return "";
  const from = query.fromBlock == null ? "" : query.fromBlock.toString();
  const to = query.toBlock == null ? "" : query.toBlock.toString();
  return `${from}..${to}::${(query.topics ?? []).join(",")}`;
}
function useEventLogs(ref, abi, opts = {}) {
  const enabled = opts.enabled ?? true;
  const reader = useContractReader(opts.reader);
  const cache = opts.cache ?? sharedEventLogCache;
  const supported = typeof reader.getLogs === "function";
  const [raw, setRaw] = React19.useState([]);
  const [isPending, setIsPending] = React19.useState(false);
  const [error, setError] = React19.useState(null);
  const [fromCache, setFromCache] = React19.useState(false);
  const [nonce, setNonce] = React19.useState(0);
  const lastNonce = React19.useRef(0);
  const ready = enabled && supported && Boolean(ref);
  const refKey = ref ? (0, contract_exports.contractRefKey)(ref) : "";
  const qKey = queryKey(opts.query);
  if (useDepsChanged([refKey, qKey, ready])) {
    setRaw([]);
    setError(null);
    setFromCache(false);
  }
  React19.useEffect(() => {
    if (!ready || !ref || !reader.getLogs) {
      setRaw([]);
      setIsPending(false);
      setError(null);
      setFromCache(false);
      return void 0;
    }
    const key = eventLogCacheKey(reader.id, ref, opts.query);
    const force = nonce !== lastNonce.current;
    lastNonce.current = nonce;
    const cached = force ? void 0 : cache.get(key);
    if (cached) {
      setRaw(cached);
      setIsPending(false);
      setError(null);
      setFromCache(true);
      return void 0;
    }
    const controller = new AbortController();
    setIsPending(true);
    setError(null);
    setFromCache(false);
    reader.getLogs(ref, opts.query, { signal: controller.signal }).then((logs2) => {
      if (controller.signal.aborted) return;
      cache.set(key, logs2);
      setRaw(logs2);
      setIsPending(false);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      setRaw([]);
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsPending(false);
    });
    return () => controller.abort();
  }, [reader, ready, refKey, qKey, nonce]);
  const logs = React19.useMemo(() => {
    if (raw.length === 0) return [];
    return (0, abi_exports.decodeLogs)(raw, abi ?? EMPTY_ABI).sort((a, b) => {
      const block = Number(b.blockNumber ?? 0n) - Number(a.blockNumber ?? 0n);
      return block !== 0 ? block : (b.logIndex ?? 0) - (a.logIndex ?? 0);
    });
  }, [raw, abi]);
  const summary = React19.useMemo(() => (0, abi_exports.summarizeLogs)(logs), [logs]);
  const refetch = React19.useCallback(() => setNonce((n) => n + 1), []);
  return { logs, summary, isPending, error, supported, fromCache, refetch };
}

// src/ui/labels.ts
var PROXY_KIND_LABELS = {
  [contract_exports.ProxyKind.EIP1967]: "EIP-1967",
  [contract_exports.ProxyKind.UUPS]: "UUPS",
  [contract_exports.ProxyKind.BEACON]: "Beacon",
  [contract_exports.ProxyKind.TRANSPARENT]: "Transparent",
  [contract_exports.ProxyKind.DIAMOND]: "Diamond",
  [contract_exports.ProxyKind.CUSTOM]: "Custom",
  [contract_exports.ProxyKind.UNKNOWN]: "Unknown"
};
function proxyKindLabel(kind) {
  return PROXY_KIND_LABELS[kind] ?? PROXY_KIND_LABELS[contract_exports.ProxyKind.UNKNOWN];
}
var ABI_SOURCE_LABELS = {
  [AbiSourceKind.INLINE]: "Inline",
  [AbiSourceKind.CACHE]: "Cache",
  [AbiSourceKind.CATALOG]: "Catalog",
  [AbiSourceKind.DEFAULTS]: "Defaults",
  [AbiSourceKind.EXTERNAL]: "External"
};
function abiSourceLabel(source) {
  return ABI_SOURCE_LABELS[source] ?? source;
}
function formatReadValue(value) {
  if (value === null || value === void 0) return "\u2014";
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "boolean") return value ? "true" : "false";
  if (Array.isArray(value)) return `[${value.map(formatReadValue).join(", ")}]`;
  if (typeof value === "object") {
    return JSON.stringify(value, (_k, v) => typeof v === "bigint" ? v.toString() : v, 2);
  }
  return String(value);
}
function coerceArg(param, raw) {
  const type = param.type;
  const label = param.name && param.name.length > 0 ? `${param.name} (${type})` : type;
  const trimmed = raw.trim();
  if (type !== "string" && trimmed === "") {
    throw new Error(`${label} is required.`);
  }
  if (type.endsWith("]") || type.startsWith("tuple")) {
    return JSON.parse(trimmed);
  }
  if (type.startsWith("uint") || type.startsWith("int")) {
    try {
      return BigInt(trimmed);
    } catch {
      throw new Error(`${label} must be a whole number.`);
    }
  }
  if (type === "bool") {
    const lowered = trimmed.toLowerCase();
    if (lowered === "true" || lowered === "1") return true;
    if (lowered === "false" || lowered === "0") return false;
    throw new Error(`${label} must be true or false.`);
  }
  return trimmed;
}
function buildArgs(inputs, values) {
  if (!inputs || inputs.length === 0) return [];
  return inputs.map((param, i) => {
    const key = param.name && param.name.length > 0 ? param.name : `arg${i}`;
    return coerceArg(param, values[key] ?? "");
  });
}
var OMITTED_READS_NAMED = 4;
function omittedReadsNote(needArguments) {
  if (needArguments.length === 0) return null;
  const named = needArguments.slice(0, OMITTED_READS_NAMED).map((fn) => `${fn.name}()`).join(", ");
  const more = needArguments.length > OMITTED_READS_NAMED ? ", \u2026" : "";
  const plural = needArguments.length === 1;
  return `${needArguments.length} more read ${plural ? "function takes" : "functions take"} arguments (${named}${more}) \u2014 call ${plural ? "it" : "them"} on the Read tab.`;
}
function FnRow({ fn }) {
  let selector = "";
  try {
    selector = (0, abi_exports.functionSelector)(fn);
  } catch {
    selector = "";
  }
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3 border-b py-1.5 last:border-0", children: [
    /* @__PURE__ */ jsx("code", { className: "truncate font-mono text-xs", children: (0, abi_exports.functionSignature)(fn) }),
    /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-2", children: [
      /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "font-mono text-[10px]", children: fn.stateMutability }),
      selector && /* @__PURE__ */ jsx("code", { className: "text-[10px] text-muted-foreground", children: selector })
    ] })
  ] });
}
function EventRow({ ev }) {
  let topic = "";
  try {
    topic = (0, abi_exports.eventTopic)(ev);
  } catch {
    topic = "";
  }
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3 border-b py-1.5 last:border-0", children: [
    /* @__PURE__ */ jsx("code", { className: "truncate font-mono text-xs", children: (0, abi_exports.eventSignature)(ev) }),
    topic && /* @__PURE__ */ jsxs("code", { className: "shrink-0 text-[10px] text-muted-foreground", children: [
      topic.slice(0, 10),
      "\u2026"
    ] })
  ] });
}
function AbiViewer({ abi, className, collapsed = false }) {
  if (!abi || abi.entries.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No ABI", description: "Resolve, paste, or pin an ABI to inspect its interface.", size: "sm" });
  }
  const sections = [
    {
      id: "reads",
      label: "Read functions",
      count: abi.reads.length,
      body: abi.reads.map((fn, i) => /* @__PURE__ */ jsx(FnRow, { fn }, `${fn.name}-${i}`))
    },
    {
      id: "writes",
      label: "Write functions",
      count: abi.writes.length,
      body: abi.writes.map((fn, i) => /* @__PURE__ */ jsx(FnRow, { fn }, `${fn.name}-${i}`))
    },
    {
      id: "events",
      label: "Events",
      count: abi.events.length,
      body: abi.events.map((ev, i) => /* @__PURE__ */ jsx(EventRow, { ev }, `${ev.name}-${i}`))
    },
    {
      id: "errors",
      label: "Errors",
      count: abi.errors.length,
      // The signature, not the bare name: a custom error is what a revert
      // decodes against, and its parameter types are the half that tells you
      // whether the revert you are holding matches this one.
      body: abi.errors.map((err, i) => /* @__PURE__ */ jsx("div", { className: "border-b py-1.5 last:border-0", children: /* @__PURE__ */ jsx("code", { className: "font-mono text-xs", children: (0, abi_exports.errorSignature)(err) }) }, `${err.name}-${i}`))
    }
  ];
  const defaultValue = collapsed ? [] : ["reads", "writes"];
  return /* @__PURE__ */ jsx(Accordion, { type: "multiple", defaultValue, className: cn("w-full", className), children: sections.map((s) => /* @__PURE__ */ jsxs(AccordionItem, { value: s.id, children: [
    /* @__PURE__ */ jsx(AccordionTrigger, { className: "text-sm", children: /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2", children: [
      s.label,
      /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "text-[10px]", children: s.count })
    ] }) }),
    /* @__PURE__ */ jsx(AccordionContent, { children: s.count === 0 ? /* @__PURE__ */ jsx("p", { className: "py-2 text-xs text-muted-foreground", children: "None." }) : /* @__PURE__ */ jsx("div", { className: "px-1", children: s.body }) })
  ] }, s.id)) });
}
function InlineAbiEditor({
  initialEntries,
  onSave,
  onClear,
  hasOverride = false,
  height = 280,
  onPersist,
  persistLabel = "Save to workspace",
  persistBusy = false,
  name,
  onNameChange,
  scopeOptions,
  scope,
  onScopeChange,
  notice,
  className
}) {
  const seed = React19.useMemo(
    () => initialEntries && initialEntries.length > 0 ? JSON.stringify(initialEntries, null, 2) : "",
    [initialEntries]
  );
  const [text, setText] = React19.useState(seed);
  React19.useEffect(() => setText(seed), [seed]);
  const { abi, error } = React19.useMemo(() => (0, abi_exports.parseAbiJson)(text), [text]);
  const showError = text.trim().length > 0 && error != null;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    onPersist && onNameChange && /* @__PURE__ */ jsx(Field, { label: "Name", labelClassName: "text-xs", children: /* @__PURE__ */ jsx(
      Input,
      {
        value: name ?? "",
        onChange: (e) => onNameChange(e.target.value),
        placeholder: "e.g. USDC, MyToken \u2014 labels this ABI in your registry",
        disabled: persistBusy,
        className: "h-8 text-sm"
      }
    ) }),
    /* @__PURE__ */ jsx(JsonEditor, { value: text, onChange: setText, height }),
    showError && /* @__PURE__ */ jsx(Alert, { variant: "destructive", children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: error?.message }) }),
    notice,
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: abi ? `${abi.reads.length + abi.writes.length} functions \xB7 ${abi.events.length} events` : "Paste a JSON ABI array." }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        hasOverride && onClear && /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: onClear, children: "Remove override" }),
        /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", disabled: !abi, onClick: () => abi && onSave(abi.entries), children: "Use locally" }),
        onPersist && scopeOptions && scopeOptions.length > 0 && /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-1.5 text-xs text-muted-foreground", children: [
          /* @__PURE__ */ jsx("span", { className: "whitespace-nowrap", children: "Save for" }),
          /* @__PURE__ */ jsx(
            NativeSelect,
            {
              value: scope ?? scopeOptions[0]?.value,
              onChange: (e) => onScopeChange?.(e.target.value),
              disabled: persistBusy,
              density: "sm",
              "aria-label": "Scope to save the ABI for",
              children: scopeOptions.map((o) => /* @__PURE__ */ jsx("option", { value: o.value, children: o.label }, o.value))
            }
          )
        ] }),
        onPersist && /* @__PURE__ */ jsx(Button, { size: "sm", disabled: !abi || persistBusy, onClick: () => abi && onPersist(abi.entries), children: persistBusy ? "Saving\u2026" : persistLabel })
      ] })
    ] })
  ] });
}
var SOURCE_ORDER_HINTS = {
  [AbiSourceKind.CATALOG]: "Your org / tenant / user ABIs.",
  [AbiSourceKind.DEFAULTS]: "Generic ERC interfaces \u2014 a guess, never this contract's own.",
  [AbiSourceKind.EXTERNAL]: "Etherscan-like explorers."
};
var SOURCE_TOGGLE = {
  [AbiSourceKind.CATALOG]: "useCatalog",
  [AbiSourceKind.DEFAULTS]: "useDefaults",
  [AbiSourceKind.EXTERNAL]: "useExternalSources"
};
var TOGGLES = [
  { key: "useCatalog", label: "Catalog ABIs", hint: "Org / tenant / user catalog (identity precedence)." },
  { key: "useDefaults", label: "Default ABIs", hint: "Fallback to ERC-20/721 and well-known interfaces." },
  { key: "useExternalSources", label: "External sources", hint: "Etherscan-like explorers." },
  { key: "useProxies", label: "Follow proxies", hint: "Use the implementation ABI for calls." },
  { key: "discoverProxies", label: "Discover proxies", hint: "Detect proxies via storage-slot reads." }
];
function AbiSourcePanel({
  options,
  onChange,
  onReset,
  origin,
  meta,
  isGuess = false,
  className
}) {
  const order = React19.useMemo(
    () => providerSourceOrder(options.sourcePriority),
    [options.sourcePriority]
  );
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold", children: "Resolution" }),
      onReset && /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: onReset, children: "Reset" })
    ] }),
    (origin || meta) && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-xs text-muted-foreground", children: [
        origin && /* @__PURE__ */ jsx(Badge, { variant: isGuess ? "destructive" : "secondary", children: abiSourceLabel(origin) }),
        meta?.contractName && /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: meta.contractName }),
        meta?.isProxy && /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "proxy" }),
        typeof meta?.confidence === "number" && /* @__PURE__ */ jsxs("span", { title: isGuess ? "A fixed floor for the fallback, not a measurement of this contract." : void 0, children: [
          isGuess ? "fallback confidence" : "confidence",
          " ",
          Math.round(meta.confidence * 100),
          "%"
        ] }),
        meta?.lifecycle && /* @__PURE__ */ jsxs("span", { children: [
          "\xB7 ",
          meta.lifecycle
        ] })
      ] }),
      (meta?.explanation ?? []).length > 0 && /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-0.5 text-xs text-muted-foreground", children: (meta?.explanation ?? []).map((line, i) => /* @__PURE__ */ jsxs("li", { children: [
        "\u2014 ",
        line
      ] }, `${i}-${line}`)) })
    ] }),
    /* @__PURE__ */ jsx(Separator, {}),
    /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-3", children: TOGGLES.map((row) => /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "space-y-0.5", children: [
        /* @__PURE__ */ jsx(Label, { htmlFor: `abi-opt-${String(row.key)}`, className: "text-sm", children: row.label }),
        /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: row.hint })
      ] }),
      /* @__PURE__ */ jsx(
        Switch,
        {
          id: `abi-opt-${String(row.key)}`,
          checked: Boolean(options[row.key]),
          onCheckedChange: (checked) => onChange({ [row.key]: checked })
        }
      )
    ] }, String(row.key))) }),
    /* @__PURE__ */ jsx(Separator, {}),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "space-y-0.5", children: [
        /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold", children: "Source order" }),
        /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "First match wins. A local edit and the session cache are always checked before any of these." })
      ] }),
      /* @__PURE__ */ jsx("ol", { className: "flex flex-col divide-y rounded-md border", children: order.map((source, i) => {
        const toggle = SOURCE_TOGGLE[source];
        const off = toggle ? !options[toggle] : false;
        const label = abiSourceLabel(source);
        return /* @__PURE__ */ jsxs("li", { className: "flex items-center justify-between gap-3 px-3 py-2", children: [
          /* @__PURE__ */ jsxs("div", { className: "min-w-0 space-y-0.5", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx("span", { className: "font-mono text-[11px] tabular-nums text-muted-foreground", children: i + 1 }),
              /* @__PURE__ */ jsx("span", { className: cn("text-sm", off && "text-muted-foreground line-through"), children: label }),
              off && /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-[10px]", children: "off" })
            ] }),
            /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: SOURCE_ORDER_HINTS[source] })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-1", children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "sm",
                className: "h-7 w-7 p-0",
                "aria-label": `Move ${label} earlier`,
                disabled: i === 0,
                onClick: () => onChange({ sourcePriority: moveAbiSource(options.sourcePriority, source, -1) }),
                children: /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: "\u2191" })
              }
            ),
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "sm",
                className: "h-7 w-7 p-0",
                "aria-label": `Move ${label} later`,
                disabled: i === order.length - 1,
                onClick: () => onChange({ sourcePriority: moveAbiSource(options.sourcePriority, source, 1) }),
                children: /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: "\u2193" })
              }
            )
          ] })
        ] }, source);
      }) })
    ] })
  ] });
}
function windowLabel(entry) {
  if (entry.validFromBlock == null && entry.validToBlock == null) return null;
  return formatBlockWindow({
    valid_from_block: entry.validFromBlock ?? 0,
    valid_to_block: entry.validToBlock ?? null
  });
}
function badgeVariant(entry) {
  if (entry.origin === "catalog") return "outline";
  if (entry.isDefault) return "secondary";
  if (entry.inherited) return "outline";
  return "default";
}
function AbiSourcesPanel({
  sources,
  isLoading = false,
  error = null,
  onRetry,
  onUse,
  onTogglePin,
  activeSourceId,
  pinnedIds,
  block = null,
  className
}) {
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: Array.from({ length: 3 }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-14 w-full" }, i)) });
  }
  if (error) return /* @__PURE__ */ jsx(ErrorState, { error, onRetry });
  if (sources.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: block == null ? "No ABIs registered" : `No ABIs live at block ${block.toLocaleString()}`,
        description: block == null ? "No saved or catalog ABIs for this contract yet. Paste one in the Edit tab and save it to your workspace." : "This contract has no ABI whose block window covers the pinned block. Clear the block pin to see every version.",
        size: "sm"
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-1", className), children: [
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: block == null ? "Every ABI version visible to you, newest catalog row included." : `ABIs live at block ${block.toLocaleString()}.` }),
    /* @__PURE__ */ jsx("ul", { className: "divide-y divide-border", children: sources.map((entry) => {
      const id = abiSourceId(entry);
      const isActive = activeSourceId != null && id === activeSourceId;
      const isPinned = pinnedIds?.has(id) ?? false;
      const hasEntries = Boolean(entry.entries && entry.entries.length > 0);
      const entriesMissing = !hasEntries && entry.entriesCount > 0;
      const entriesEmpty = !hasEntries && entry.entriesCount === 0;
      return /* @__PURE__ */ jsxs("li", { className: "flex items-center justify-between gap-3 py-2.5", children: [
        /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx("span", { className: "truncate text-sm font-medium text-foreground", children: entry.name || (entry.origin === "catalog" ? "Catalog ABI" : "Unnamed ABI") }),
            /* @__PURE__ */ jsx(Badge, { variant: badgeVariant(entry), className: "text-[10px]", children: abiSourceScopeLabel(entry) })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground", children: [
            /* @__PURE__ */ jsxs("span", { children: [
              entry.entriesCount,
              " items"
            ] }),
            windowLabel(entry) && /* @__PURE__ */ jsxs("span", { className: "tabular-nums", children: [
              "\xB7 ",
              windowLabel(entry)
            ] }),
            entry.source && /* @__PURE__ */ jsxs("span", { children: [
              "\xB7 ",
              entry.source
            ] }),
            entry.isProxy && /* @__PURE__ */ jsx("span", { children: "\xB7 proxy" })
          ] }),
          entriesMissing && /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-xs text-destructive", children: "Entries couldn't be loaded, so this ABI can't be used or pinned. Retry, or check the stored blob for this contract." }),
          entriesEmpty && /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-xs text-muted-foreground", children: "This ABI is registered but declares nothing, so there is nothing to use or pin." })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-2", children: [
          onTogglePin && /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: isPinned ? "secondary" : "ghost",
              disabled: !hasEntries,
              onClick: () => hasEntries && onTogglePin(entry),
              children: isPinned ? "Pinned" : "Pin"
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: isActive ? "secondary" : "outline",
              disabled: !hasEntries || !onUse || isActive,
              onClick: () => hasEntries && onUse?.(entry),
              children: isActive ? "In use" : "Use"
            }
          )
        ] })
      ] }, id);
    }) })
  ] });
}
function NoAbiState({ subject, failures, className }) {
  const failed = failures && failures.length > 0;
  return /* @__PURE__ */ jsx(
    EmptyState,
    {
      title: failed ? "Couldn't load an ABI" : "No ABI resolved",
      description: failed ? `${describeAbiFailures(failures)} So we don't know whether this contract has ${subject}. Retry, or paste an ABI in the Edit tab.` : `Without an ABI we can't tell what ${subject} this contract has. Resolve or paste one in the Edit tab.`,
      size: "sm",
      className
    }
  );
}
function GuessedAbiNotice({
  consequence,
  onFetch,
  fetching = false,
  className
}) {
  return /* @__PURE__ */ jsx(Alert, { className: cn("border-risk-medium/40 bg-risk-medium/5", className), children: /* @__PURE__ */ jsxs(AlertDescription, { className: "flex flex-col gap-2 text-xs", children: [
    /* @__PURE__ */ jsxs("span", { children: [
      /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: "Showing the standard ERC-20/721 interfaces \u2014 not this contract's own ABI." }),
      " ",
      "No stored, catalogued or verified ABI was found for this address."
    ] }),
    /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
      "Values you see are read from the real contract and are accurate. What is missing is everything beyond the standard: this contract's own functions, events and errors aren't listed here, and its logs can't be decoded.",
      consequence ? ` ${consequence}` : ""
    ] }),
    onFetch && /* @__PURE__ */ jsx("span", { children: /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", disabled: fetching, onClick: onFetch, children: fetching ? "Looking\u2026" : "Try fetching the ABI" }) })
  ] }) });
}
var TRUNCATION_NOTE = {
  cycle: "The implementation pointer loops back to an address already in this chain. The last new implementation is shown; anything past it repeats.",
  "max-hops": "The hop limit was reached with another proxy still ahead. There may be a further implementation this view does not show.",
  "beacon-unresolved": "A beacon was found but this reader cannot call it for its implementation, so the address below is the BEACON \u2014 its ABI is a stub, not the contract's.",
  "probe-failed": "At least one detection strategy failed while walking this contract. The result below is what the strategies that did run reported \u2014 treat it as incomplete, not as a finding."
};
function TruncationNote({ chain }) {
  if (!chain.truncated) return null;
  return /* @__PURE__ */ jsx("p", { className: "rounded-md border border-risk-medium/40 bg-risk-medium/5 p-2 text-xs text-muted-foreground", children: TRUNCATION_NOTE[chain.truncated] });
}
function ExplanationNote({ chain }) {
  if (!chain.explanation || chain.explanation.length === 0) return null;
  return /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-0.5 text-xs text-muted-foreground", children: chain.explanation.map((line, i) => /* @__PURE__ */ jsxs("li", { children: [
    "\u2014 ",
    line
  ] }, `${i}-${line}`)) });
}
function ProxyChainView({
  chain,
  chainId,
  isResolving = false,
  error = null,
  onRetry,
  onSelectImplementation,
  className
}) {
  if (isResolving) {
    return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 py-6 text-sm text-muted-foreground", children: [
      /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      " Resolving proxy chain\u2026"
    ] });
  }
  if (error) {
    const forbidden = /403|forbidden|scope/i.test(error.message);
    return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
      /* @__PURE__ */ jsx(
        EmptyState,
        {
          title: forbidden ? "You can't see proxy data" : "Couldn't resolve the proxy chain",
          description: forbidden ? "Reading proxy resolutions needs the proxy:resolve permission. Whether this contract delegates to an implementation is unknown from here \u2014 it is not a finding that it doesn't." : `${error.message} \u2014 so we don't know whether this contract is a proxy. This is not the same as it not being one.`,
          size: "sm"
        }
      ),
      onRetry && !forbidden && /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: onRetry, children: "Retry" }) })
    ] });
  }
  if (!chain) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No contract", description: "Select a contract to inspect its proxy chain.", size: "sm" });
  }
  if (!chain.isProxy) {
    const incomplete = Boolean(chain.truncated);
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 py-2 text-sm", children: [
        /* @__PURE__ */ jsx(Badge, { variant: "outline", children: incomplete ? "inconclusive" : "direct" }),
        /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: incomplete ? "No implementation found." : "Not a proxy." }),
        /* @__PURE__ */ jsx(AddressDisplay, { address: chain.root, chain: chainId })
      ] }),
      /* @__PURE__ */ jsx(TruncationNote, { chain }),
      /* @__PURE__ */ jsx(ExplanationNote, { chain })
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-1.5", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "rounded-md border bg-muted/30 p-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "mb-1 flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: "proxy" }),
        /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "root" })
      ] }),
      /* @__PURE__ */ jsx(AddressDisplay, { address: chain.root, chain: chainId })
    ] }),
    chain.hops.map((hop, i) => {
      const isEffective = hop.address.toLowerCase() === chain.effectiveAddress.toLowerCase();
      return /* @__PURE__ */ jsxs(React19.Fragment, { children: [
        /* @__PURE__ */ jsx("span", { className: "mx-auto text-sm leading-none text-muted-foreground", "aria-hidden": true, children: "\u2193" }),
        /* @__PURE__ */ jsxs("div", { className: cn("rounded-md border p-2", isEffective && "border-primary/50 bg-primary/5"), children: [
          /* @__PURE__ */ jsxs("div", { className: "mb-1 flex items-center justify-between gap-2", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx(Badge, { variant: "outline", children: proxyKindLabel(hop.kind) }),
              isEffective && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "effective" })
            ] }),
            onSelectImplementation && !isEffective && /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: () => onSelectImplementation(hop.address), children: "Use" })
          ] }),
          /* @__PURE__ */ jsx(AddressDisplay, { address: hop.address, chain: chainId }),
          hop.beacon && /* @__PURE__ */ jsxs("p", { className: "mt-1 text-[10px] text-muted-foreground", children: [
            "via beacon ",
            /* @__PURE__ */ jsxs("code", { className: "font-mono", children: [
              hop.beacon.slice(0, 10),
              "\u2026"
            ] })
          ] })
        ] })
      ] }, `${hop.address}-${i}`);
    }),
    /* @__PURE__ */ jsx(TruncationNote, { chain }),
    /* @__PURE__ */ jsx(ExplanationNote, { chain })
  ] });
}
var RUNG_OPTIONS = [
  { value: "user", label: "Only me" },
  { value: "tenant", label: "Tenant" },
  { value: "org", label: "Organization" },
  { value: "customer", label: "Entire customer" }
];
var PATTERNS = ["manual", "eip1967", "uups", "beacon", "transparent", "diamond", "custom"];
function implAddresses(impls) {
  if (!Array.isArray(impls)) return [];
  return impls.map((i) => typeof i === "string" ? i : i.address).filter(Boolean);
}
function discoveredRow(w, i) {
  return {
    key: `d-${w.valid_from_block}-${i}`,
    kind: "discovered",
    valid_from_block: w.valid_from_block,
    valid_to_block: w.valid_to_block,
    implementations: implAddresses(w.implementations),
    pattern: w.pattern ?? null,
    scope: null,
    ...toProvenance(w)
  };
}
function overrideRow(o) {
  return {
    key: `o-${o.override_id}`,
    kind: "override",
    valid_from_block: o.valid_from_block,
    valid_to_block: o.valid_to_block,
    implementations: o.implementations ?? [],
    pattern: o.pattern ?? null,
    scope: o.scope,
    ...toProvenance(o)
  };
}
function viewAtLabel(window) {
  const target = windowViewBlock(window);
  return target === null ? "View at current" : `View at ${Number(target).toLocaleString()}`;
}
function timelineRows(history) {
  if (!history) return [];
  const rows = [
    ...history.windows.map(discoveredRow),
    ...history.overrides.map(overrideRow)
  ];
  return rows.sort((a, b) => {
    if (a.valid_from_block !== b.valid_from_block) return a.valid_from_block - b.valid_from_block;
    return a.kind === b.kind ? 0 : a.kind === "discovered" ? -1 : 1;
  });
}
function ProxyOverridePanel({
  chainId,
  address,
  client,
  canEdit = false,
  canPurge = false,
  canIndex = false,
  writeScopes = [],
  block = null,
  onBlockChange,
  className
}) {
  const resolution = useScopedOverride(
    {
      cacheFragment: "/proxy-overrides/",
      read: (c, { scope: scope2, block: at, signal }) => c.get(`/proxy-overrides/${encodeURIComponent(chainId)}/${encodeURIComponent(address)}`, {
        query: {
          ...scope2 ? { scope: scope2 } : {},
          ...at != null ? { block: at.toString() } : {}
        },
        signal,
        cacheTtlMs: 0
      }),
      write: canEdit ? (c, { input, scope: scope2 }) => c.put(
        `/proxy-overrides/${encodeURIComponent(chainId)}/${encodeURIComponent(address)}`,
        // The client's own body builder, not the raw form state: an empty
        // "Until block" is `null` here, and API Gateway's draft-4 validator
        // rejects an explicit null before the handler ever runs.
        proxyOverrideRequestBody(input),
        { query: scope2 ? { scope: scope2 } : {} }
      ) : void 0,
      // `?block=` picks ONE window; without it the service deletes every window
      // this rung holds. The button says "Remove my pin", singular, so it has to
      // send the block it is showing.
      remove: (c, { scope: scope2, block: at }) => c.delete(`/proxy-overrides/${encodeURIComponent(chainId)}/${encodeURIComponent(address)}`, {
        query: {
          ...scope2 ? { scope: scope2 } : {},
          ...at != null ? { block: at.toString() } : {}
        }
      })
    },
    { initialBlock: block }
  );
  const { setBlock } = resolution;
  React19.useEffect(() => {
    setBlock(block);
  }, [block, setBlock]);
  const setPin = React19.useCallback(
    (next) => {
      setBlock(next);
      onBlockChange?.(next);
    },
    [setBlock, onBlockChange]
  );
  const scope = resolution.scope;
  const historyKey = `${chainId}|${address}|${scope ?? ""}`;
  const [loaded, setLoaded] = React19.useState(
    void 0
  );
  const [historyError, setHistoryError] = React19.useState(null);
  const [loadingHistory, setLoadingHistory] = React19.useState(false);
  const history = loaded?.key === historyKey ? loaded.data : void 0;
  const historySeq = React19.useRef(0);
  const loadHistory = React19.useCallback(async () => {
    const seq = ++historySeq.current;
    const key = historyKey;
    setLoadingHistory(true);
    setHistoryError(null);
    try {
      const data = await client.history(chainId, address, { scope });
      if (seq !== historySeq.current) return;
      setLoaded({ key, data });
    } catch (err) {
      if (seq !== historySeq.current) return;
      setHistoryError(err instanceof Error ? err.message : String(err));
    } finally {
      if (seq === historySeq.current) setLoadingHistory(false);
    }
  }, [client, chainId, address, scope, historyKey]);
  const loadHistoryRef = React19.useRef(loadHistory);
  React19.useEffect(() => {
    loadHistoryRef.current = loadHistory;
  }, [loadHistory]);
  React19.useEffect(() => {
    void loadHistoryRef.current();
  }, [historyKey]);
  const rows = React19.useMemo(() => timelineRows(history), [history]);
  const [isProxy, setIsProxy] = React19.useState(true);
  const [implText, setImplText] = React19.useState("");
  const [pattern, setPattern] = React19.useState("manual");
  const [fromBlock, setFromBlock] = React19.useState("");
  const [toBlock, setToBlock] = React19.useState("");
  const [reason, setReason] = React19.useState("");
  const [evidenceUrl, setEvidenceUrl] = React19.useState("");
  const [targetRung, setTargetRung] = React19.useState(void 0);
  React19.useEffect(() => {
    setFromBlock(block === null ? "" : block.toString());
  }, [block]);
  const implementations = React19.useMemo(
    () => implText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
    [implText]
  );
  const formError = React19.useMemo(() => {
    if (isProxy && implementations.length === 0) return "A proxy needs at least one implementation address.";
    if (!isProxy && implementations.length > 0) return "Remove the implementations to pin a non-proxy.";
    if (fromBlock && !/^\d+$/.test(fromBlock.trim())) return "From-block must be a block number.";
    if (toBlock && !/^\d+$/.test(toBlock.trim())) return "To-block must be a block number.";
    if (fromBlock && toBlock && Number(toBlock) <= Number(fromBlock)) {
      return "To-block must be greater than from-block.";
    }
    return null;
  }, [isProxy, implementations, fromBlock, toBlock]);
  const windowNote = React19.useMemo(() => {
    const from = fromBlock.trim() ? Number(fromBlock.trim()) : 0;
    if (!toBlock.trim()) {
      return `Applies from block ${from} onward, including the current block.`;
    }
    const to = Number(toBlock.trim());
    if (!Number.isFinite(to) || to <= from) return null;
    return `Applies to blocks ${from}-${to - 1} only. At the current block this contract falls back to discovery \u2014 clear the field to pin the present.`;
  }, [fromBlock, toBlock]);
  const submit = React19.useCallback(async () => {
    if (formError) return;
    const input = {
      is_proxy: isProxy,
      implementations,
      pattern,
      valid_from_block: fromBlock ? Number(fromBlock) : 0,
      valid_to_block: toBlock ? Number(toBlock) : null,
      reason: reason.trim() || void 0,
      evidence_url: evidenceUrl.trim() || void 0
    };
    const writeRung = targetRung ?? null;
    if (targetRung) resolution.setScope(targetRung);
    const written = await resolution.save(input, { scope: writeRung });
    if (written) {
      const rung = writeRung ? RUNG_OPTIONS.find((o) => o.value === writeRung)?.label : null;
      notifySuccess(rung ? `Proxy pinned \xB7 ${rung}` : "Proxy pinned", {
        description: input.valid_to_block == null ? "Reads and decodes for this scope now use this implementation." : `Applies to blocks ${input.valid_from_block}-${input.valid_to_block - 1}. Reads at the current block fall back to discovery \u2014 leave "Until block" blank to pin the present.`
      });
      void loadHistory();
    }
  }, [
    formError,
    isProxy,
    implementations,
    pattern,
    fromBlock,
    toBlock,
    reason,
    evidenceUrl,
    targetRung,
    resolution,
    loadHistory
  ]);
  const myPins = React19.useMemo(() => rows.filter((r) => r.kind === "override"), [rows]);
  const pinnedAt = resolution.block;
  const removesEverything = pinnedAt == null && myPins.length > 1;
  const [unpinOpen, setUnpinOpen] = React19.useState(false);
  const unpin = React19.useCallback(async () => {
    if (await resolution.remove({ block: pinnedAt })) {
      notifySuccess(
        pinnedAt == null ? "Pins removed" : `Pin removed \xB7 block ${pinnedAt.toLocaleString()}`,
        {
          description: pinnedAt == null ? "Every pin your scope held for this contract is gone; resolution falls back to on-chain discovery." : "Resolution at this block falls back to the next rung down, then to on-chain discovery."
        }
      );
      void loadHistory();
    }
  }, [resolution, loadHistory, pinnedAt]);
  const [indexing, setIndexing] = React19.useState(false);
  const requestIndex = React19.useCallback(async () => {
    setIndexing(true);
    try {
      const result = await client.index(chainId, address);
      if (result.accepted) {
        notifySuccess("History build queued", {
          description: "Scanning upgrade events takes a moment \u2014 reload the timeline shortly."
        });
      } else {
        notifyError(new Error("The service did not accept the request."), {
          title: "Couldn't queue the history build."
        });
      }
    } catch (err) {
      notifyError(err, { title: "Couldn't queue the history build." });
    } finally {
      setIndexing(false);
    }
  }, [client, chainId, address]);
  const [purging, setPurging] = React19.useState(false);
  const [purgeOpen, setPurgeOpen] = React19.useState(false);
  const purge = React19.useCallback(async () => {
    setPurging(true);
    try {
      await client.purge(chainId, address);
      notifySuccess("Discovery cache purged", { description: "The next read re-detects from chain." });
      resolution.refetch();
      void loadHistory();
    } catch (err) {
      notifyError(err, { title: "Couldn't purge the discovery cache." });
    } finally {
      setPurging(false);
    }
  }, [client, chainId, address, resolution, loadHistory]);
  const elsewhere = React19.useMemo(
    () => rows.filter((r) => r.implementations.length > 0 && !windowCovers(r, resolution.block)).sort((a, b) => b.valid_from_block - a.valid_from_block),
    [rows, resolution.block]
  );
  const myElsewhere = React19.useMemo(() => elsewhere.filter((r) => r.kind === "override"), [elsewhere]);
  const nearest = myElsewhere[0] ?? elsewhere[0];
  const resolved = resolution.data;
  const fromOverride = resolved?.resolved_by === "proxy_override";
  const rungLabel = resolved?.scope ? scopeRungLabel(resolved.scope) : resolved?.scope_rung ?? null;
  const provenance = toProvenance(resolved);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "rounded-md border p-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "mb-2 flex flex-wrap items-center justify-between gap-2", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("span", { className: "text-xs font-medium", children: "Resolved" }),
          resolution.isFetching && /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
          rungLabel && /* @__PURE__ */ jsx(Badge, { variant: fromOverride ? "secondary" : "outline", className: "text-[10px]", children: fromOverride ? `Pinned \xB7 ${rungLabel}` : rungLabel })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(BlockPinControl, { id: "proxy-block", value: resolution.block, onChange: setPin }),
          writeScopes.length > 1 && /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-1.5 text-xs text-muted-foreground", children: [
            /* @__PURE__ */ jsx("span", { className: "whitespace-nowrap", children: "View as" }),
            /* @__PURE__ */ jsxs(
              NativeSelect,
              {
                value: scope ?? "",
                onChange: (e) => resolution.setScope(e.target.value || null),
                density: "sm",
                "aria-label": "Tenancy rung to read the proxy at",
                children: [
                  /* @__PURE__ */ jsx("option", { value: "", children: "My scope" }),
                  RUNG_OPTIONS.filter((o) => writeScopes.includes(o.value)).map((o) => /* @__PURE__ */ jsx("option", { value: o.value, children: o.label }, o.value))
                ]
              }
            )
          ] })
        ] })
      ] }),
      resolution.error ? /* @__PURE__ */ jsx("p", { className: "text-xs text-destructive", children: resolution.error.error.message }) : resolved?.is_proxy === false ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Not a proxy at this block." }),
        loadingHistory && !history && /* @__PURE__ */ jsxs("p", { className: "flex items-center gap-1.5 text-xs text-muted-foreground", children: [
          /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
          " Checking other blocks\u2026"
        ] }),
        nearest && /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
          myElsewhere.length > 0 ? `Your pin applies to ${formatBlockWindow(nearest)}, which does not include this block.` : `${elsewhere.length} other window${elsewhere.length === 1 ? "" : "s"} name an implementation.`,
          " ",
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "ghost",
              size: "sm",
              className: "h-5 px-1 text-[11px]",
              onClick: () => setPin(windowViewBlock(nearest)),
              children: viewAtLabel(nearest)
            }
          )
        ] })
      ] }) : /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1.5", children: (resolved?.implementations ?? []).map((impl, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-[10px]", children: impl.pattern ?? resolved?.pattern ?? "proxy" }),
        /* @__PURE__ */ jsx(AddressDisplay, { address: impl.address, chain: chainId })
      ] }, `${impl.address}-${i}`)) }),
      /* @__PURE__ */ jsx(
        WhyPanel,
        {
          className: "mt-3 border-t pt-2",
          explanation: provenance.explanation,
          evidence: provenance.evidence,
          confidence: provenance.confidence,
          lifecycle: provenance.lifecycle
        }
      ),
      fromOverride && canPurge && /* @__PURE__ */ jsx("div", { className: "mt-2 flex justify-end", children: removesEverything ? /* @__PURE__ */ jsxs(AlertDialog, { open: unpinOpen, onOpenChange: (next) => !resolution.isSaving && setUnpinOpen(next), children: [
        /* @__PURE__ */ jsx(AlertDialogTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", disabled: resolution.isSaving, children: "Remove my pins" }) }),
        /* @__PURE__ */ jsxs(AlertDialogContent, { children: [
          /* @__PURE__ */ jsxs(AlertDialogHeader, { children: [
            /* @__PURE__ */ jsxs(AlertDialogTitle, { children: [
              "Remove all ",
              myPins.length,
              " of your pins for this contract?"
            ] }),
            /* @__PURE__ */ jsxs(AlertDialogDescription, { children: [
              "You are viewing the current block, so there is no single window to remove \u2014 this deletes every pin your scope holds for ",
              /* @__PURE__ */ jsx("span", { className: "font-mono", children: address }),
              ", across its whole upgrade history. To remove just one, pin the block it covers first."
            ] })
          ] }),
          /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [
            /* @__PURE__ */ jsx(AlertDialogCancel, { disabled: resolution.isSaving, children: "Cancel" }),
            /* @__PURE__ */ jsxs(
              Button,
              {
                type: "button",
                variant: "destructive",
                disabled: resolution.isSaving,
                onClick: () => {
                  setUnpinOpen(false);
                  void unpin();
                },
                children: [
                  "Remove ",
                  myPins.length,
                  " pins"
                ]
              }
            )
          ] })
        ] })
      ] }) : /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", disabled: resolution.isSaving, onClick: () => void unpin(), children: pinnedAt == null ? "Remove my pin" : `Remove my pin at ${pinnedAt.toLocaleString()}` }) }),
      fromOverride && !canPurge && /* @__PURE__ */ jsxs("p", { className: "mt-2 text-right text-[11px] text-muted-foreground", children: [
        "Removing a pin needs the ",
        /* @__PURE__ */ jsx("code", { className: "font-mono", children: "proxy:delete" }),
        " permission."
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "rounded-md border p-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-center justify-between gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-xs font-medium", children: "Upgrade timeline" }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          loadingHistory && /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
          /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", disabled: loadingHistory, onClick: () => void loadHistory(), children: "Reload" })
        ] })
      ] }),
      historyError && /* @__PURE__ */ jsx("p", { className: "text-xs text-destructive", children: historyError }),
      loadingHistory && !history && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Loading upgrade history and your pins\u2026" }),
      history && rows.length === 0 && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
        /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
          "No upgrade history indexed for this contract yet.",
          !canIndex && /* @__PURE__ */ jsxs(Fragment, { children: [
            " ",
            "Building one needs the ",
            /* @__PURE__ */ jsx("code", { className: "font-mono", children: "proxy:index" }),
            " permission."
          ] })
        ] }),
        canIndex && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", disabled: indexing, onClick: () => void requestIndex(), children: indexing ? "Requesting\u2026" : "Build history" })
      ] }),
      /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-1.5", children: rows.map((row) => {
        const active = windowCovers(row, resolution.block);
        return /* @__PURE__ */ jsxs(
          "li",
          {
            className: cn(
              "rounded-md border p-2 text-xs",
              row.kind === "override" && "border-primary/40 bg-primary/5",
              active && "ring-1 ring-primary/40"
            ),
            children: [
              /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
                /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                  /* @__PURE__ */ jsx(Badge, { variant: row.kind === "override" ? "secondary" : "outline", className: "text-[10px]", children: row.kind === "override" ? scopeRungLabel(row.scope) ?? "Pinned" : "Chain" }),
                  /* @__PURE__ */ jsx("span", { className: "tabular-nums text-muted-foreground", children: formatBlockWindow(row) })
                ] }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    variant: "ghost",
                    size: "sm",
                    className: "h-6 px-2 text-[11px]",
                    onClick: () => setPin(windowViewBlock(row)),
                    children: viewAtLabel(row)
                  }
                )
              ] }),
              /* @__PURE__ */ jsx("div", { className: "mt-1 flex flex-col gap-1", children: row.implementations.length === 0 ? /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: row.kind === "override" ? "pinned as not a proxy" : "no proxy detected" }) : row.implementations.map((impl, i) => /* @__PURE__ */ jsx(AddressDisplay, { address: impl, chain: chainId }, `${impl}-${i}`)) }),
              /* @__PURE__ */ jsx(
                WhyPanel,
                {
                  className: "mt-2",
                  title: "Why this window?",
                  explanation: row.explanation,
                  evidence: row.evidence,
                  confidence: row.confidence,
                  lifecycle: row.lifecycle
                }
              )
            ]
          },
          row.key
        );
      }) }),
      (history?.overrides_truncated_rungs?.length ?? 0) > 0 && /* @__PURE__ */ jsxs("p", { className: "mt-2 rounded-md border border-risk-medium/40 bg-risk-medium/5 p-2 text-xs text-muted-foreground", children: [
        "More pins exist than are shown \u2014 the",
        " ",
        history?.overrides_truncated_rungs?.map((rung) => scopeRungLabel(rung) ?? rung).join(", "),
        " ",
        (history?.overrides_truncated_rungs?.length ?? 0) === 1 ? "rung was" : "rungs were",
        " truncated at the service's cap. Narrow the view with \u201CView as\u201D to see a single rung in full."
      ] })
    ] }),
    canEdit && /* @__PURE__ */ jsxs("div", { className: "rounded-md border p-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-center justify-between gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-xs font-medium", children: "Pin an implementation" }),
        /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-1.5 text-xs text-muted-foreground", children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "checkbox",
              checked: !isProxy,
              onChange: (e) => {
                setIsProxy(!e.target.checked);
                if (e.target.checked) setImplText("");
              }
            }
          ),
          "Not a proxy"
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "grid gap-2 sm:grid-cols-2", children: [
        /* @__PURE__ */ jsx(Field, { label: "Implementation address(es)", className: "sm:col-span-2", labelClassName: "text-xs", children: /* @__PURE__ */ jsx(
          Textarea,
          {
            value: implText,
            onChange: (e) => setImplText(e.target.value),
            disabled: !isProxy,
            rows: 2,
            placeholder: "0x\u2026 (several for a diamond, comma or space separated)",
            className: "font-mono text-xs"
          }
        ) }),
        /* @__PURE__ */ jsx(Field, { label: "Pattern", labelClassName: "text-xs", children: /* @__PURE__ */ jsx(
          NativeSelect,
          {
            value: pattern,
            onChange: (e) => setPattern(e.target.value),
            density: "sm",
            children: PATTERNS.map((p) => /* @__PURE__ */ jsx("option", { value: p, children: p }, p))
          }
        ) }),
        writeScopes.length > 1 && /* @__PURE__ */ jsx(Field, { label: "Applies to", labelClassName: "text-xs", children: /* @__PURE__ */ jsxs(
          NativeSelect,
          {
            value: targetRung ?? "",
            onChange: (e) => setTargetRung(e.target.value || void 0),
            density: "sm",
            children: [
              /* @__PURE__ */ jsx("option", { value: "", children: "My scope" }),
              RUNG_OPTIONS.filter((o) => writeScopes.includes(o.value)).map((o) => /* @__PURE__ */ jsx("option", { value: o.value, children: o.label }, o.value))
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(Field, { label: "From block", labelClassName: "text-xs", children: /* @__PURE__ */ jsx(
          Input,
          {
            value: fromBlock,
            onChange: (e) => setFromBlock(e.target.value),
            inputMode: "numeric",
            placeholder: "0",
            className: "h-8 font-mono text-xs"
          }
        ) }),
        /* @__PURE__ */ jsx(
          Field,
          {
            label: /* @__PURE__ */ jsxs(Fragment, { children: [
              "Until block",
              " ",
              /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "(exclusive, blank = current)" })
            ] }),
            labelClassName: "text-xs",
            children: /* @__PURE__ */ jsx(
              Input,
              {
                value: toBlock,
                onChange: (e) => setToBlock(e.target.value),
                inputMode: "numeric",
                placeholder: "current",
                className: "h-8 font-mono text-xs"
              }
            )
          }
        ),
        windowNote && /* @__PURE__ */ jsx("p", { className: "text-[11px] text-muted-foreground sm:col-span-2", children: windowNote }),
        /* @__PURE__ */ jsx(Field, { label: "Reason", className: "sm:col-span-2", labelClassName: "text-xs", children: /* @__PURE__ */ jsx(
          Input,
          {
            value: reason,
            onChange: (e) => setReason(e.target.value),
            placeholder: "Why the detectors are wrong here",
            className: "h-8 text-xs"
          }
        ) }),
        /* @__PURE__ */ jsx(
          Field,
          {
            label: /* @__PURE__ */ jsxs(Fragment, { children: [
              "Evidence link ",
              /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "(optional)" })
            ] }),
            className: "sm:col-span-2",
            labelClassName: "text-xs",
            children: /* @__PURE__ */ jsx(
              Input,
              {
                value: evidenceUrl,
                onChange: (e) => setEvidenceUrl(e.target.value),
                placeholder: "https://\u2026 upgrade tx, ticket, explorer page",
                className: "h-8 text-xs"
              }
            )
          }
        )
      ] }),
      formError && /* @__PURE__ */ jsx(Alert, { variant: "destructive", className: "mt-2", children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: formError }) }),
      resolution.saveError && /* @__PURE__ */ jsx(Alert, { variant: "destructive", className: "mt-2", children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: resolution.saveError.error.message }) }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-[11px] text-muted-foreground", children: "A pin decides which ABI your transactions decode against, and reaches everyone at the rung you choose. Its confidence is set by your scope's policy, not by this form." }),
      /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-center justify-end gap-2", children: [
        canPurge && /* @__PURE__ */ jsxs(AlertDialog, { open: purgeOpen, onOpenChange: (next) => !purging && setPurgeOpen(next), children: [
          /* @__PURE__ */ jsx(AlertDialogTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", disabled: purging, children: purging ? "Purging\u2026" : "Purge shared cache" }) }),
          /* @__PURE__ */ jsxs(AlertDialogContent, { children: [
            /* @__PURE__ */ jsxs(AlertDialogHeader, { children: [
              /* @__PURE__ */ jsx(AlertDialogTitle, { children: "Purge the shared discovery cache for this contract?" }),
              /* @__PURE__ */ jsxs(AlertDialogDescription, { children: [
                "This deletes the stored detection rows for",
                " ",
                /* @__PURE__ */ jsx("span", { className: "font-mono", children: address }),
                " for everyone, not just your scope. The next read re-detects from chain, which costs RPC and can resolve differently. Pins \u2014 yours and other scopes' \u2014 are not touched."
              ] })
            ] }),
            /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [
              /* @__PURE__ */ jsx(AlertDialogCancel, { disabled: purging, children: "Cancel" }),
              /* @__PURE__ */ jsx(
                Button,
                {
                  type: "button",
                  variant: "destructive",
                  disabled: purging,
                  onClick: () => {
                    setPurgeOpen(false);
                    void purge();
                  },
                  children: "Purge cache"
                }
              )
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx(Button, { size: "sm", disabled: Boolean(formError) || resolution.isSaving, onClick: () => void submit(), children: resolution.isSaving ? "Pinning\u2026" : "Pin implementation" })
      ] })
    ] }),
    !canEdit && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Separator, {}),
      /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
        "You need the ",
        /* @__PURE__ */ jsx("code", { className: "font-mono", children: "proxy:edit" }),
        " permission to pin an implementation."
      ] })
    ] })
  ] });
}
function fieldKey(param, i) {
  return param.name && param.name.length > 0 ? param.name : `arg${i}`;
}
function FunctionForm({
  fn,
  actionLabel,
  busy = false,
  disabled = false,
  onSubmit,
  className
}) {
  const [values, setValues] = React19.useState({});
  const [valueWei, setValueWei] = React19.useState("");
  const [error, setError] = React19.useState(null);
  const inputs = fn.inputs ?? [];
  const isPayable = fn.stateMutability === "payable";
  const submit = React19.useCallback(() => {
    setError(null);
    try {
      const args = buildArgs(inputs, values);
      const value = isPayable && valueWei.trim() ? BigInt(valueWei.trim()) : void 0;
      onSubmit(args, value);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [inputs, values, isPayable, valueWei, onSubmit]);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    inputs.length > 0 && /* @__PURE__ */ jsx("div", { className: "grid gap-2", children: inputs.map((param, i) => {
      const key = fieldKey(param, i);
      return /* @__PURE__ */ jsx(
        Field,
        {
          label: /* @__PURE__ */ jsxs(Fragment, { children: [
            key,
            " ",
            /* @__PURE__ */ jsx("span", { className: "font-mono", children: param.type })
          ] }),
          labelClassName: "text-xs text-muted-foreground",
          children: /* @__PURE__ */ jsx(
            Input,
            {
              value: values[key] ?? "",
              placeholder: param.type,
              onChange: (e) => setValues((v) => ({ ...v, [key]: e.target.value })),
              className: "h-8 font-mono text-xs"
            }
          )
        },
        key
      );
    }) }),
    isPayable && /* @__PURE__ */ jsx(
      Field,
      {
        label: /* @__PURE__ */ jsxs(Fragment, { children: [
          "value ",
          /* @__PURE__ */ jsx("span", { className: "font-mono", children: "wei" })
        ] }),
        labelClassName: "text-xs text-muted-foreground",
        children: /* @__PURE__ */ jsx(
          Input,
          {
            value: valueWei,
            placeholder: "0",
            onChange: (e) => setValueWei(e.target.value),
            className: "h-8 font-mono text-xs"
          }
        )
      }
    ),
    error && /* @__PURE__ */ jsx(Alert, { variant: "destructive", children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: error }) }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx("code", { className: "truncate text-[10px] text-muted-foreground", children: (0, abi_exports.functionSignature)(fn) }),
      /* @__PURE__ */ jsx(Button, { size: "sm", onClick: submit, disabled: disabled || busy, children: busy ? "\u2026" : actionLabel })
    ] })
  ] });
}
function ReadFunctionCard({
  fn,
  contractRef,
  abi,
  reader
}) {
  const [args, setArgs] = React19.useState(
    (fn.inputs ?? []).length === 0 ? [] : null
  );
  const { data, error, isPending } = useReaderRead({
    reader,
    ref: contractRef,
    abi,
    functionName: fn.name,
    args
  });
  return /* @__PURE__ */ jsxs("div", { className: "rounded-md border p-3", children: [
    /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-center gap-2", children: [
      /* @__PURE__ */ jsx("code", { className: "font-mono text-sm font-medium", children: fn.name }),
      /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-[10px]", children: fn.stateMutability })
    ] }),
    /* @__PURE__ */ jsx(FunctionForm, { fn, actionLabel: "Call", busy: isPending, onSubmit: (a) => setArgs(a) }),
    data !== void 0 && /* @__PURE__ */ jsx("pre", { className: "mt-2 overflow-x-auto rounded bg-muted/50 p-2 text-xs", children: formatReadValue(data) }),
    error && /* @__PURE__ */ jsx("p", { className: "mt-2 text-xs text-destructive", children: error.message })
  ] });
}
function ReadPanel({
  contract: contractRef,
  abi,
  isGuess = false,
  failures,
  onFetchAbi,
  fetchingAbi,
  reader: readerOverride,
  className
}) {
  const reader = useContractReader(readerOverride);
  const [filter, setFilter] = React19.useState("");
  const reads = React19.useMemo(() => {
    if (!abi) return [];
    const q = filter.trim().toLowerCase();
    return q ? abi.reads.filter((fn) => fn.name.toLowerCase().includes(q)) : abi.reads;
  }, [abi, filter]);
  if (!abi) return /* @__PURE__ */ jsx(NoAbiState, { subject: "read functions", failures, className });
  if (abi.reads.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No read functions", description: "This ABI exposes no view/pure functions.", size: "sm" });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    isGuess && /* @__PURE__ */ jsx(GuessedAbiNotice, { onFetch: onFetchAbi, fetching: fetchingAbi, consequence: "Reads the contract doesn't implement will revert." }),
    /* @__PURE__ */ jsx(
      Input,
      {
        value: filter,
        placeholder: "Filter functions\u2026",
        onChange: (e) => setFilter(e.target.value),
        className: "h-8"
      }
    ),
    /* @__PURE__ */ jsx(ScrollArea, { className: "max-h-[480px] pr-2", children: /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", children: reads.map((fn) => /* @__PURE__ */ jsx(
      ReadFunctionCard,
      {
        fn,
        contractRef,
        abi,
        reader
      },
      (0, abi_exports.functionSignature)(fn)
    )) }) })
  ] });
}
var STATUS_LABEL = {
  idle: "",
  signing: "Awaiting signature\u2026",
  pending: "Pending\u2026",
  sent: "Sent \u2014 not confirmed",
  success: "Confirmed",
  reverted: "Reverted on chain",
  error: "Failed"
};
var FAILED_STATUSES = /* @__PURE__ */ new Set([
  "error",
  "reverted"
]);
function WriteFunctionCard({
  fn,
  contractRef,
  abi,
  chainId,
  disabled = false
}) {
  const { write, status, hash, error } = useContractWrite(chainId);
  const onSubmit = React19.useCallback(
    (args, value) => {
      void write({ address: contractRef.address, abi: abi.entries, functionName: fn.name, args }, value);
    },
    [write, contractRef.address, abi.entries, fn.name]
  );
  const busy = status === "signing" || status === "pending";
  return /* @__PURE__ */ jsxs("div", { className: "rounded-md border p-3", children: [
    /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("code", { className: "font-mono text-sm font-medium", children: fn.name }),
        /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-[10px]", children: fn.stateMutability })
      ] }),
      status !== "idle" && /* @__PURE__ */ jsx(
        Badge,
        {
          variant: FAILED_STATUSES.has(status) ? "destructive" : status === "success" ? "secondary" : "outline",
          className: "text-[10px]",
          children: STATUS_LABEL[status]
        }
      )
    ] }),
    /* @__PURE__ */ jsx(FunctionForm, { fn, actionLabel: "Send", busy, disabled, onSubmit }),
    hash && /* @__PURE__ */ jsxs("p", { className: "mt-2 text-[10px] text-muted-foreground", children: [
      "tx ",
      /* @__PURE__ */ jsxs("code", { className: "font-mono", children: [
        hash.slice(0, 12),
        "\u2026"
      ] })
    ] }),
    error && /* @__PURE__ */ jsx("p", { className: "mt-2 text-xs text-destructive", children: error.message })
  ] });
}
function WritePanel({
  contract: contractRef,
  abi,
  isGuess = false,
  failures,
  onFetchAbi,
  fetchingAbi,
  className
}) {
  const [filter, setFilter] = React19.useState("");
  const writes = React19.useMemo(() => {
    if (!abi) return [];
    const q = filter.trim().toLowerCase();
    return q ? abi.writes.filter((fn) => fn.name.toLowerCase().includes(q)) : abi.writes;
  }, [abi, filter]);
  const chainIdNum = (0, contract_exports.chainIdToNumber)(contractRef.chainId);
  if (!abi) return /* @__PURE__ */ jsx(NoAbiState, { subject: "write functions", failures, className });
  if (abi.writes.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No write functions", description: "This ABI exposes no state-changing functions.", size: "sm" });
  }
  if (chainIdNum === void 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Can't sign on this chain",
        description: `"${contractRef.chainId}" doesn't resolve to a known EVM chain id, so there is no way to be sure a signature would go to this network rather than whichever one your wallet is on. Reads are unaffected.`,
        size: "sm",
        className
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    isGuess && /* @__PURE__ */ jsx(GuessedAbiNotice, { onFetch: onFetchAbi, fetching: fetchingAbi, consequence: "Sending is disabled until a real ABI is resolved \u2014 these selectors may not exist on this contract." }),
    /* @__PURE__ */ jsx(Input, { value: filter, placeholder: "Filter functions\u2026", onChange: (e) => setFilter(e.target.value), className: "h-8" }),
    /* @__PURE__ */ jsx(ScrollArea, { className: "max-h-[480px] pr-2", children: /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", children: writes.map((fn) => /* @__PURE__ */ jsx(
      WriteFunctionCard,
      {
        fn,
        contractRef,
        abi,
        chainId: chainIdNum,
        disabled: isGuess
      },
      (0, abi_exports.functionSignature)(fn)
    )) }) })
  ] });
}
function ContractFieldsGrid({
  contract: contractRef,
  abi,
  isGuess = false,
  failures,
  onFetchAbi,
  fetchingAbi,
  className
}) {
  const { fields, needArguments, isPending, error, refetch } = useContractFields(
    contractRef,
    abi
  );
  const argNote = omittedReadsNote(needArguments);
  if (!abi) return /* @__PURE__ */ jsx(NoAbiState, { subject: "fields", failures, className });
  if (fields.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No fields",
        description: argNote ? `Nothing here can be read without arguments. ${argNote}` : "No zero-argument view functions to read.",
        size: "sm"
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    isGuess && /* @__PURE__ */ jsx(GuessedAbiNotice, { onFetch: onFetchAbi, fetching: fetchingAbi, consequence: "Getters this contract doesn't implement will show as reverts." }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold", children: "Fields" }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-xs text-muted-foreground", children: [
        isPending && /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
        /* @__PURE__ */ jsx("button", { type: "button", className: "hover:text-foreground", onClick: refetch, children: "Refresh" })
      ] })
    ] }),
    error && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2", children: [
      /* @__PURE__ */ jsxs("p", { className: "text-xs text-destructive", children: [
        "Couldn't read these fields: ",
        error.message
      ] }),
      /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: refetch, children: "Retry" })
    ] }),
    argNote && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: argNote }),
    /* @__PURE__ */ jsx("div", { className: "divide-y rounded-md border", children: fields.map(({ fn, outcome }, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3 px-3 py-2", children: [
      /* @__PURE__ */ jsx("code", { className: "font-mono text-xs font-medium", children: fn.name }),
      /* @__PURE__ */ jsx("div", { className: "min-w-0 flex-1 text-right", children: outcome?.status === "success" ? /* @__PURE__ */ jsx("code", { className: "break-all text-xs", children: formatReadValue(outcome.data) }) : outcome?.status === "failure" ? /* @__PURE__ */ jsx(Badge, { variant: "destructive", className: "text-[10px]", children: "revert" }) : /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "\u2014" }) })
    ] }, `${fn.name}-${i}`)) })
  ] });
}
var GROUPS = {
  function: "Functions",
  event: "Events",
  error: "Errors"
};
function CompareAbisView({ items, className }) {
  const { columns, rows } = React19.useMemo(() => {
    const sets = /* @__PURE__ */ new Map();
    const union = /* @__PURE__ */ new Set();
    for (const item of items) {
      const set = (0, abi_exports.signatureSet)(item.abi);
      sets.set(item.id, set);
      for (const sig of set) union.add(sig);
    }
    const cols = items.map((item) => ({
      id: item.id,
      header: item.label,
      subject: item.id
    }));
    const sigRows = [...union].sort().map((sig) => {
      const [kind, ...rest] = sig.split(":");
      const signature = rest.join(":");
      return {
        id: sig,
        // Three kinds, not two. Errors were absent from `signatureSet` entirely,
        // so a version that added a revert error compared as identical; grouping
        // them with functions would have hidden the difference a second way.
        group: GROUPS[kind ?? ""] ?? "Functions",
        label: /* @__PURE__ */ jsx("code", { className: "text-xs", children: signature }),
        value: (subject) => sets.get(subject)?.has(sig) ? "1" : "0",
        render: (subject) => sets.get(subject)?.has(sig) ? /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "text-[10px]", children: "\u2713" }) : /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "\u2014" })
      };
    });
    return { columns: cols, rows: sigRows };
  }, [items]);
  if (items.length < 2) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Pin at least two ABIs",
        description: "Pin ABIs (different contracts, blocks, or chains) to diff their interfaces.",
        size: "sm"
      }
    );
  }
  return /* @__PURE__ */ jsx(CompareView, { columns, rows, className });
}
function FieldsLoader({
  columnId,
  contractRef,
  abi,
  onValues
}) {
  const { fields } = useContractFields(contractRef, abi);
  const onValuesRef = React19.useRef(onValues);
  onValuesRef.current = onValues;
  React19.useEffect(() => {
    const map = {};
    for (const f of fields) {
      if (f.outcome?.status === "success") map[f.fn.name] = formatReadValue(f.outcome.data);
      else if (f.outcome?.status === "failure") map[f.fn.name] = "revert";
    }
    onValuesRef.current(columnId, map);
  }, [columnId, fields]);
  return null;
}
function CompareFieldsView({ refs, abi, className }) {
  const [values, setValues] = React19.useState({});
  const setColumn = React19.useCallback((columnId, map) => {
    setValues((prev) => ({ ...prev, [columnId]: map }));
  }, []);
  const columnsMeta = React19.useMemo(
    () => refs.map((r, i) => ({ id: `${(0, contract_exports.contractRefKey)(r)}#${i}`, ref: r })),
    [refs]
  );
  if (!abi || refs.length < 2) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Pick at least two",
        description: "Compare field values across blocks, contracts, or chains.",
        size: "sm"
      }
    );
  }
  const fieldNames = abi.reads.filter((fn) => (fn.inputs ?? []).length === 0).map((fn) => fn.name);
  const columns = columnsMeta.map((c) => ({
    id: c.id,
    header: c.ref.label ?? `${c.ref.address.slice(0, 8)}\u2026 @ ${(0, contract_exports.blockKey)(c.ref.block)}`,
    subject: c.id
  }));
  const rows = fieldNames.map((name) => ({
    id: name,
    label: /* @__PURE__ */ jsx("code", { className: "text-xs", children: name }),
    value: (subject) => values[subject]?.[name] ?? "",
    render: (subject) => /* @__PURE__ */ jsx("code", { className: "text-xs", children: values[subject]?.[name] ?? "\u2014" })
  }));
  return /* @__PURE__ */ jsxs("div", { className, children: [
    columnsMeta.map((c) => /* @__PURE__ */ jsx(FieldsLoader, { columnId: c.id, contractRef: c.ref, abi, onValues: setColumn }, c.id)),
    /* @__PURE__ */ jsx(CompareView, { columns, rows })
  ] });
}

// src/state/diff.ts
function diffContractState(before, after) {
  const fields = /* @__PURE__ */ new Set([...Object.keys(before.values), ...Object.keys(after.values)]);
  const rows = [];
  for (const field of fields) {
    const b = before.values[field];
    const a = after.values[field];
    const hasB = b !== void 0;
    const hasA = a !== void 0;
    let change;
    if (hasB && !hasA) change = "removed";
    else if (!hasB && hasA) change = "added";
    else if (b !== a) change = "changed";
    else change = "unchanged";
    rows.push({ field, before: b, after: a, change });
  }
  return rows.sort((x, y) => x.field.localeCompare(y.field));
}
function changedRows(rows) {
  return rows.filter((r) => r.change !== "unchanged");
}
function changeCount(rows) {
  return changedRows(rows).length;
}
function diffContractStates(snapshots) {
  const fields = /* @__PURE__ */ new Set();
  for (const snap of snapshots) for (const key of Object.keys(snap.values)) fields.add(key);
  const rows = [];
  for (const field of fields) {
    const cells = [];
    let changed = false;
    let prev;
    snapshots.forEach((snap, i) => {
      const cur = snap.values[field];
      let change;
      if (i === 0) {
        change = "unchanged";
      } else {
        const hasPrev = prev !== void 0;
        const hasCur = cur !== void 0;
        if (hasPrev && !hasCur) change = "removed";
        else if (!hasPrev && hasCur) change = "added";
        else if (cur !== prev) change = "changed";
        else change = "unchanged";
      }
      if (change !== "unchanged") changed = true;
      cells.push({ value: cur, change });
      prev = cur;
    });
    rows.push({ field, cells, changed });
  }
  return rows.sort((a, b) => a.field.localeCompare(b.field));
}
function changedMultiRows(rows) {
  return rows.filter((r) => r.changed);
}
var CELL_STYLES = {
  added: "bg-finality-finalized/10",
  removed: "bg-risk-high/10",
  changed: "bg-risk-medium/10",
  unchanged: ""
};
function resolveSnapshots(props) {
  if (props.snapshots && props.snapshots.length > 0) return props.snapshots;
  return [props.before, props.after, props.latest].filter(
    (s) => s != null
  );
}
function DiffTable({
  rows,
  headers
}) {
  if (rows.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No differences", description: "The snapshots have identical field values.", size: "sm" });
  }
  return /* @__PURE__ */ jsx("div", { className: "scroll-fade-y max-h-[480px] overflow-auto rounded-md border", children: /* @__PURE__ */ jsxs(Table, { className: "w-full text-sm", children: [
    /* @__PURE__ */ jsx(TableHeader, { className: "sticky top-0 bg-card", children: /* @__PURE__ */ jsxs(TableRow, { className: "border-b text-left text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx(TableHead, { className: "px-3 py-2 font-medium", children: "Field" }),
      headers.map((h, i) => /* @__PURE__ */ jsx(TableHead, { className: "px-3 py-2 font-medium", children: h }, i))
    ] }) }),
    /* @__PURE__ */ jsx(TableBody, { children: rows.map((row) => /* @__PURE__ */ jsxs(TableRow, { className: "border-b align-top", children: [
      /* @__PURE__ */ jsx(TableCell, { className: "px-3 py-2", children: /* @__PURE__ */ jsx("code", { className: "text-xs", children: row.field }) }),
      row.cells.map((cell, i) => /* @__PURE__ */ jsx(TableCell, { className: cn("px-3 py-2", CELL_STYLES[cell.change]), children: /* @__PURE__ */ jsx(
        "code",
        {
          className: cn(
            "break-all text-xs",
            cell.change === "unchanged" ? "text-muted-foreground" : "text-foreground"
          ),
          children: cell.value ?? "\u2014"
        }
      ) }, i))
    ] }, row.field)) })
  ] }) });
}
function ContractStateHighlighter(props) {
  const { className, snapshots: given, before, after, latest } = props;
  const snapshots = React19.useMemo(
    () => resolveSnapshots({ snapshots: given, before, after, latest }),
    [given, before, after, latest]
  );
  const rows = React19.useMemo(() => diffContractStates(snapshots), [snapshots]);
  const changed = React19.useMemo(() => changedMultiRows(rows), [rows]);
  if (snapshots.length < 2) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Pick at least two snapshots",
        description: "Compare a contract's state across blocks (before / after / latest).",
        size: "sm",
        className
      }
    );
  }
  const headers = snapshots.map((snap, i) => snap.label ?? ["Before", "After", "Latest"][i] ?? `#${i + 1}`);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2 text-sm", children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: headers.join(" \u2192 ") }),
      /* @__PURE__ */ jsxs(Badge, { variant: changed.length > 0 ? "secondary" : "outline", children: [
        changed.length,
        " ",
        changed.length === 1 ? "change" : "changes"
      ] })
    ] }),
    /* @__PURE__ */ jsxs(Tabs, { defaultValue: "changes", children: [
      /* @__PURE__ */ jsxs(TabsList, { children: [
        /* @__PURE__ */ jsxs(TabsTrigger, { value: "changes", children: [
          "Changes (",
          changed.length,
          ")"
        ] }),
        /* @__PURE__ */ jsxs(TabsTrigger, { value: "all", children: [
          "All fields (",
          rows.length,
          ")"
        ] })
      ] }),
      /* @__PURE__ */ jsx(TabsContent, { value: "changes", className: "mt-3", children: /* @__PURE__ */ jsx(DiffTable, { rows: changed, headers }) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "all", className: "mt-3", children: /* @__PURE__ */ jsx(DiffTable, { rows, headers }) })
    ] })
  ] });
}
function blockLabel(node) {
  if (node.block_number != null) return `#${node.block_number}`;
  if (node.block_tag) return node.block_tag;
  return null;
}
function isAddressArray(value) {
  return Array.isArray(value) && value.every((v) => typeof v === "string");
}
function LongValue({ text }) {
  return /* @__PURE__ */ jsx("code", { className: "line-clamp-3 break-all font-mono text-xs", title: text, children: text });
}
function StateValueDisplay({
  value,
  chainId
}) {
  if (value.error) {
    return /* @__PURE__ */ jsx("span", { className: "text-xs text-destructive", children: value.error });
  }
  const decoded = value.value_decoded;
  if (value.is_address) {
    if (isAddressArray(decoded) && decoded.length > 0) {
      return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1", children: decoded.map((addr, i) => /* @__PURE__ */ jsx(AddressDisplay, { address: addr, chain: chainId, truncate: { head: 10, tail: 6 } }, `${addr}-${i}`)) });
    }
    if (typeof decoded === "string" && decoded) {
      return /* @__PURE__ */ jsx(AddressDisplay, { address: decoded, chain: chainId, truncate: { head: 10, tail: 6 } });
    }
  }
  if (value.display) {
    return /* @__PURE__ */ jsx(LongValue, { text: value.display });
  }
  if (decoded == null) return /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "\u2014" });
  if (typeof decoded === "object") {
    return /* @__PURE__ */ jsx("pre", { className: "overflow-x-auto rounded bg-muted/40 p-1.5 font-mono text-[11px] leading-snug", children: JSON.stringify(decoded, null, 2) });
  }
  return /* @__PURE__ */ jsx(LongValue, { text: String(decoded) });
}
function ValueRow({
  value,
  chainId
}) {
  const name = value.label ?? value.key;
  const params = value.params ?? [];
  return /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-[minmax(8rem,12rem)_1fr] gap-2 py-1", children: [
    /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
      /* @__PURE__ */ jsx("span", { className: "truncate font-medium text-foreground", children: name }),
      params.length > 0 && /* @__PURE__ */ jsxs("span", { className: "ml-1 font-mono text-[10px] text-muted-foreground", children: [
        "(",
        params.map((p) => String(p)).join(", "),
        ")"
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "min-w-0", children: /* @__PURE__ */ jsx(StateValueDisplay, { value, chainId }) })
  ] });
}
function CombinedRow({ combined }) {
  const name = combined.label ?? `${combined.strategy}${combined.over ? ` \xB7 ${combined.over}` : ""}`;
  return /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-[minmax(8rem,12rem)_1fr] gap-2 py-1", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
      /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-[10px]", children: String(combined.strategy) }),
      /* @__PURE__ */ jsx("span", { className: "truncate text-xs text-muted-foreground", children: name })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "min-w-0", children: /* @__PURE__ */ jsx(LongValue, { text: combined.display ?? JSON.stringify(combined.value) }) })
  ] });
}
function ContractStateNodeCard({
  node,
  depth = 0,
  className
}) {
  const [childrenOpen, setChildrenOpen] = React19.useState(depth < 1);
  const block = blockLabel(node);
  const hasChildren = node.children.length > 0;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "rounded-md border bg-card p-2.5",
        node.is_proxy && "border-primary/40",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
          /* @__PURE__ */ jsx(
            AddressDisplay,
            {
              address: node.address,
              chain: node.chain_id,
              label: node.name ?? void 0,
              truncate: { head: 10, tail: 6 }
            }
          ),
          !node.is_contract && /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-[10px] text-muted-foreground", children: "EOA" }),
          node.is_contract && node.category && /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "text-[10px]", children: node.category }),
          node.is_proxy && /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-[10px]", children: [
            "proxy",
            node.proxy_pattern ? ` \xB7 ${node.proxy_pattern}` : ""
          ] }),
          block && /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "font-mono text-[10px]", children: block }),
          node.truncated && /* @__PURE__ */ jsx(Badge, { variant: "destructive", className: "text-[10px]", children: "truncated" })
        ] }),
        !node.is_contract && /* @__PURE__ */ jsx("p", { className: "mt-1.5 text-[11px] text-muted-foreground", children: "Externally-owned account \u2014 no on-chain contract state." }),
        node.is_proxy && node.implementation_addresses.length > 0 && /* @__PURE__ */ jsxs("div", { className: "mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground", children: [
          /* @__PURE__ */ jsx("span", { children: "impl:" }),
          node.implementation_addresses.map((addr, i) => /* @__PURE__ */ jsx(AddressDisplay, { address: addr, chain: node.chain_id, truncate: { head: 8, tail: 6 } }, `${addr}-${i}`))
        ] }),
        node.errors.length > 0 && /* @__PURE__ */ jsx("ul", { className: "mt-1.5 space-y-0.5", children: node.errors.map((err, i) => /* @__PURE__ */ jsx("li", { className: "text-xs text-destructive", children: err }, i)) }),
        node.values.length > 0 && /* @__PURE__ */ jsx("div", { className: "mt-2 divide-y divide-border/60 text-sm", children: node.values.map((value) => /* @__PURE__ */ jsx(ValueRow, { value, chainId: node.chain_id }, value.ref)) }),
        node.combined.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Separator, { className: "my-2" }),
          /* @__PURE__ */ jsx("div", { className: "divide-y divide-border/60", children: node.combined.map((combined, i) => /* @__PURE__ */ jsx(CombinedRow, { combined }, `${combined.strategy}-${i}`)) })
        ] }),
        hasChildren && /* @__PURE__ */ jsxs("div", { className: "mt-2", children: [
          /* @__PURE__ */ jsxs(
            Button,
            {
              variant: "ghost",
              size: "sm",
              className: "h-6 px-1.5 text-xs text-muted-foreground",
              onClick: () => setChildrenOpen((open) => !open),
              "aria-expanded": childrenOpen,
              children: [
                /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "mr-1 inline-block w-2", children: childrenOpen ? "\u25BE" : "\u25B8" }),
                node.children.length,
                " related address",
                node.children.length === 1 ? "" : "es"
              ]
            }
          ),
          childrenOpen && /* @__PURE__ */ jsx("div", { className: "mt-1.5 space-y-1.5 border-l-2 border-border/60 pl-2.5", children: node.children.map((child) => /* @__PURE__ */ jsx(ContractStateNodeCard, { node: child, depth: depth + 1 }, child.ref)) })
        ] })
      ]
    }
  );
}
function ContractStateTree({
  result,
  isPending = false,
  error = null,
  unwired = false,
  onRefresh,
  className
}) {
  if (unwired) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Backend state not configured",
        description: "Provide a ContractStateClient (via ContractStateClientProvider or the client prop) to read on-chain state \u2014 categories, proxy-merged ABIs, drill-down and cross-chain combine \u2014 through the backend.",
        size: "sm",
        className
      }
    );
  }
  if (error) {
    return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
      /* @__PURE__ */ jsx(EmptyState, { title: "Couldn't read state", description: error.message, size: "sm" }),
      onRefresh && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: onRefresh, children: "Retry" })
    ] });
  }
  if (isPending && !result) {
    return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2 py-6 text-sm text-muted-foreground", className), children: [
      /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      " Reading on-chain state\u2026"
    ] });
  }
  if (!result || result.nodes.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No state",
        description: "The backend returned no contract state for this request.",
        size: "sm",
        className
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-[10px]", children: [
        result.rpc_calls,
        " RPC call",
        result.rpc_calls === 1 ? "" : "s"
      ] }),
      result.truncated && /* @__PURE__ */ jsx(Badge, { variant: "destructive", className: "text-[10px]", children: "truncated (budget hit)" }),
      isPending && /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      onRefresh && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "ghost", className: "ml-auto h-6 px-1.5 text-xs", onClick: onRefresh, children: "Refresh" })
    ] }),
    result.errors.length > 0 && /* @__PURE__ */ jsx("ul", { className: "space-y-0.5", children: result.errors.map((err, i) => /* @__PURE__ */ jsx("li", { className: "text-xs text-destructive", children: err }, i)) }),
    result.combined.length > 0 && /* @__PURE__ */ jsxs("div", { className: "rounded-md border bg-muted/30 p-2.5", children: [
      /* @__PURE__ */ jsx("p", { className: "mb-1.5 text-xs font-semibold text-muted-foreground", children: "Combined" }),
      /* @__PURE__ */ jsx("div", { className: "space-y-1 text-sm", children: result.combined.map((combined, i) => /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
        /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-[10px]", children: String(combined.strategy) }),
        /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: combined.label ?? combined.over ?? "" }),
        /* @__PURE__ */ jsx(
          "code",
          {
            className: "line-clamp-2 min-w-0 break-all font-mono text-xs",
            title: combined.display ?? JSON.stringify(combined.value),
            children: combined.display ?? JSON.stringify(combined.value)
          }
        )
      ] }, `${combined.strategy}-${i}`)) })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "space-y-2", children: result.nodes.map((node) => /* @__PURE__ */ jsx(ContractStateNodeCard, { node }, node.ref)) })
  ] });
}
function ContractStatePanel({
  chainId,
  address,
  block,
  includeSourceCode,
  client,
  className
}) {
  const prepared = React19.useMemo(
    () => {
      try {
        return { request: buildContractStateRequest({ chainId, address, block, includeSourceCode }), error: null };
      } catch (cause) {
        return { request: null, error: cause instanceof Error ? cause : new Error(String(cause)) };
      }
    },
    [chainId, address, block, includeSourceCode]
  );
  const { result, error, isPending, unwired, refetch } = useContractState({ request: prepared.request, client });
  return /* @__PURE__ */ jsx(
    ContractStateTree,
    {
      result,
      error: prepared.error ?? error,
      isPending,
      unwired,
      onRefresh: refetch,
      className
    }
  );
}
function SnapshotLoader({
  id,
  contractRef,
  abi,
  enabled,
  onState
}) {
  const { fields, isPending } = useContractFields(contractRef, abi, { enabled });
  const onStateRef = React19.useRef(onState);
  onStateRef.current = onState;
  React19.useEffect(() => {
    const values = {};
    for (const f of fields) {
      if (f.outcome?.status === "success") values[f.fn.name] = formatReadValue(f.outcome.data);
      else if (f.outcome?.status === "failure") values[f.fn.name] = "revert";
    }
    onStateRef.current(id, { values, pending: isPending });
  }, [id, fields, isPending]);
  return null;
}
function ContractStateView({
  chainId,
  address,
  abi,
  defaultBeforeBlock = "",
  includeLatest = true,
  className
}) {
  const [beforeText, setBeforeText] = React19.useState(defaultBeforeBlock);
  const [afterText, setAfterText] = React19.useState("");
  const [epoch, setEpoch] = React19.useState(0);
  const columns = React19.useMemo(() => {
    const column = (id, text, emptyLabel) => {
      const trimmed = text.trim();
      const block = (0, contract_exports.parseBlock)(text);
      const invalid = trimmed !== "" && !block;
      return {
        id,
        invalid,
        label: invalid ? `${emptyLabel} \xB7 unreadable block` : block ? `Block ${(0, contract_exports.blockKey)(block).replace(/^#/, "")}` : emptyLabel,
        ref: { chainId, address, block }
      };
    };
    const defs = [column("before", beforeText, "Before (latest)"), column("after", afterText, "After (latest)")];
    if (includeLatest) {
      defs.push({ id: "latest", invalid: false, label: "Latest", ref: { chainId, address, block: void 0 } });
    }
    return defs;
  }, [chainId, address, beforeText, afterText, includeLatest, epoch]);
  const invalidColumns = columns.filter((c) => c.invalid);
  const [states, setStates] = React19.useState({});
  const setColumn = React19.useCallback((id, state) => {
    setStates((prev) => ({ ...prev, [id]: state }));
  }, []);
  if (!abi) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No ABI",
        description: "Resolve or paste an ABI to read the contract's state.",
        size: "sm",
        className
      }
    );
  }
  const snapshots = columns.map((c) => ({
    label: c.label,
    values: states[c.id]?.values ?? {}
  }));
  const anyPending = columns.some((c) => states[c.id]?.pending);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-4", className), children: [
    columns.map((c) => /* @__PURE__ */ jsx(
      SnapshotLoader,
      {
        id: c.id,
        contractRef: c.ref,
        abi,
        enabled: !c.invalid,
        onState: setColumn
      },
      `${c.id}-${(0, contract_exports.blockKey)(c.ref.block)}-${c.invalid ? "x" : ""}-${epoch}`
    )),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-end gap-3", children: [
      /* @__PURE__ */ jsx(Field, { label: "Before block", labelClassName: "text-xs text-muted-foreground", children: /* @__PURE__ */ jsx(
        Input,
        {
          value: beforeText,
          onChange: (e) => setBeforeText(e.target.value),
          placeholder: "latest",
          className: "h-8 w-36 font-mono text-xs"
        }
      ) }),
      /* @__PURE__ */ jsx(Field, { label: "After block", labelClassName: "text-xs text-muted-foreground", children: /* @__PURE__ */ jsx(
        Input,
        {
          value: afterText,
          onChange: (e) => setAfterText(e.target.value),
          placeholder: "latest",
          className: "h-8 w-36 font-mono text-xs"
        }
      ) }),
      /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: () => setEpoch((n) => n + 1), children: anyPending ? /* @__PURE__ */ jsx(Spinner, { size: "sm" }) : "Refresh" })
    ] }),
    invalidColumns.length > 0 && /* @__PURE__ */ jsxs("p", { className: "rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive", children: [
      invalidColumns.length === 1 ? "One block" : "Both blocks",
      " couldn't be read as a block height or tag (",
      contract_exports.BLOCK_TAGS.join(", "),
      "), so that column was left unread rather than silently answered from the chain head."
    ] }),
    /* @__PURE__ */ jsx(ContractStateHighlighter, { snapshots })
  ] });
}
function AddressIdentityChip({
  identity,
  className
}) {
  if (!identity) return null;
  const labels = identity.labels ?? [];
  const tags = identity.tags ?? [];
  const hasAny = identity.entity_name || labels.length > 0 || tags.length > 0;
  if (!hasAny) return null;
  const sourceTitle = identity.source ? `identity source: ${identity.source}` : void 0;
  return /* @__PURE__ */ jsxs("span", { className: cn("inline-flex flex-wrap items-center gap-1", className), title: sourceTitle, children: [
    identity.entity_name && /* @__PURE__ */ jsx(Badge, { variant: "default", className: "text-[10px]", children: identity.entity_name }),
    labels.map((label) => /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "text-[10px]", children: label }, `l-${label}`)),
    tags.map((tag) => /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-[10px]", children: [
      "#",
      tag
    ] }, `t-${tag}`))
  ] });
}
function deltaClass(delta) {
  if (!delta || delta === "0") return "text-muted-foreground";
  return delta.startsWith("-") ? "text-destructive" : "text-emerald-600 dark:text-emerald-400";
}
function display(raw, formatted) {
  return formatted ?? raw ?? "\u2014";
}
function tokenKey(change) {
  return change.is_native ? "native" : change.token_address ?? "?";
}
function identityKeyLookup(identities, chainId, holder) {
  const address = holder.toLowerCase();
  return identities[`${chainId}:${address}`] ?? identities[address] ?? null;
}
function group(changes) {
  const byChain = /* @__PURE__ */ new Map();
  for (const change of changes) {
    const byHolder = byChain.get(change.chain_id) ?? /* @__PURE__ */ new Map();
    const list = byHolder.get(change.holder) ?? [];
    list.push(change);
    byHolder.set(change.holder, list);
    byChain.set(change.chain_id, byHolder);
  }
  return byChain;
}
function TokenCell({ change }) {
  if (change.is_native) {
    return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
      /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-[10px]", children: "native" }),
      change.symbol && /* @__PURE__ */ jsx("span", { className: "text-xs font-medium", children: change.symbol })
    ] });
  }
  return /* @__PURE__ */ jsx(
    AddressDisplay,
    {
      address: change.token_address ?? "",
      chain: change.chain_id,
      label: change.symbol ?? void 0,
      truncate: { head: 8, tail: 6 }
    }
  );
}
function DuringTimeline({ change }) {
  const [open, setOpen] = React19.useState(false);
  if (change.during.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: "mt-1", children: [
    /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "ghost",
        size: "sm",
        className: "h-5 px-1 text-[11px] text-muted-foreground",
        onClick: () => setOpen((o) => !o),
        "aria-expanded": open,
        children: [
          /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "mr-1 inline-block w-2", children: open ? "\u25BE" : "\u25B8" }),
          change.timeline_scope === "transaction" ? "transaction timeline" : "estimated timeline",
          " \xB7 ",
          change.during.length,
          " step",
          change.during.length === 1 ? "" : "s"
        ]
      }
    ),
    open && /* @__PURE__ */ jsx("ol", { className: "ml-3 mt-0.5 space-y-0.5 border-l border-border/60 pl-2 font-mono text-[11px]", children: change.during.map((point) => /* @__PURE__ */ jsxs("li", { className: "flex items-center gap-2", children: [
      point.log_index != null && /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
        "log#",
        point.log_index
      ] }),
      /* @__PURE__ */ jsxs("span", { className: deltaClass(point.delta_raw), children: [
        point.delta_raw.startsWith("-") ? "" : "+",
        point.delta_raw
      ] }),
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2192" }),
      /* @__PURE__ */ jsx("span", { children: point.display ?? point.balance_raw })
    ] }, point.seq)) })
  ] });
}
function ChangeRow({ change }) {
  return /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-[minmax(7rem,10rem)_1fr_auto] items-start gap-2 py-1.5", children: [
    /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
      /* @__PURE__ */ jsx(TokenCell, { change }),
      /* @__PURE__ */ jsxs("span", { className: "text-[10px] text-muted-foreground", children: [
        change.balance_scope === "transaction" ? "Transaction change" : "Whole-block change",
        change.before_block != null && change.after_block != null && ` \xB7 ${change.before_block} \u2192 ${change.after_block}`
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-1.5 font-mono text-xs", children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: display(change.before_raw, change.before_display) }),
      /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "text-muted-foreground", children: "\u2192" }),
      /* @__PURE__ */ jsx("span", { children: display(change.after_raw, change.after_display) }),
      (change.warnings ?? []).map((warning) => /* @__PURE__ */ jsx("span", { className: "w-full text-[11px] text-muted-foreground", children: warning }, warning)),
      change.error && /* @__PURE__ */ jsxs("span", { className: "text-[11px] text-destructive", children: [
        "(",
        change.error,
        ")"
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: cn("text-right font-mono text-xs", deltaClass(change.delta_raw)), children: [
      change.delta_raw && change.delta_raw !== "0" ? change.delta_display ?? `${change.delta_raw.startsWith("-") ? "" : "+"}${change.delta_raw}` : change.delta_raw === "0" ? "\xB10" : "\u2014",
      change.truncated && /* @__PURE__ */ jsx(Badge, { variant: "destructive", className: "ml-1 text-[9px]", children: "truncated" }),
      /* @__PURE__ */ jsx(DuringTimeline, { change })
    ] })
  ] });
}
function HolderCard({
  chainId,
  holder,
  changes,
  identity
}) {
  return /* @__PURE__ */ jsxs("div", { className: "rounded-md border bg-card p-2.5", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
      /* @__PURE__ */ jsx(AddressDisplay, { address: holder, chain: chainId, truncate: { head: 10, tail: 6 } }),
      /* @__PURE__ */ jsx(AddressIdentityChip, { identity })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "mt-1.5 divide-y divide-border/60", children: changes.slice().sort((a, b) => tokenKey(a).localeCompare(tokenKey(b))).map((change) => /* @__PURE__ */ jsx(ChangeRow, { change }, tokenKey(change))) })
  ] });
}
function BalanceChangesPanel({
  insight,
  isPending = false,
  error = null,
  unwired = false,
  onRefresh,
  className
}) {
  if (unwired) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Transaction insight not configured",
        description: "Provide a TransactionInsightClient (via TransactionInsightClientProvider or the client prop) to read balance diffs + identity through the backend.",
        size: "sm",
        className
      }
    );
  }
  if (error) {
    return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
      /* @__PURE__ */ jsx(EmptyState, { title: "Couldn't read balances", description: error.message, size: "sm" }),
      onRefresh && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: onRefresh, children: "Retry" })
    ] });
  }
  if (isPending && !insight) {
    return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2 py-6 text-sm text-muted-foreground", className), children: [
      /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      " Reading balances\u2026"
    ] });
  }
  if (!insight || insight.balance_changes.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No balance changes",
        description: "The backend returned no balance changes for this transaction.",
        size: "sm",
        className
      }
    );
  }
  const grouped = group(insight.balance_changes);
  const identityFor = (chainId, holder) => identityKeyLookup(insight.identities, chainId, holder);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-[10px]", children: [
        insight.rpc_calls,
        " RPC call",
        insight.rpc_calls === 1 ? "" : "s"
      ] }),
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-[10px]", children: [
        insight.balance_changes.length,
        " balance change",
        insight.balance_changes.length === 1 ? "" : "s"
      ] }),
      insight.truncated && /* @__PURE__ */ jsx(Badge, { variant: "destructive", className: "text-[10px]", children: "truncated (budget hit)" }),
      isPending && /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      onRefresh && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "ghost", className: "ml-auto h-6 px-1.5 text-xs", onClick: onRefresh, children: "Refresh" })
    ] }),
    insight.errors.length > 0 && /* @__PURE__ */ jsx("ul", { className: "space-y-0.5", children: insight.errors.map((err, i) => /* @__PURE__ */ jsx("li", { className: "text-xs text-destructive", children: err }, i)) }),
    Array.from(grouped.entries()).map(([chainId, byHolder]) => /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
      /* @__PURE__ */ jsxs("p", { className: "text-xs font-semibold text-muted-foreground", children: [
        "Chain ",
        chainId
      ] }),
      Array.from(byHolder.entries()).map(([holder, changes]) => /* @__PURE__ */ jsx(
        HolderCard,
        {
          chainId,
          holder,
          changes,
          identity: identityFor(chainId, holder)
        },
        holder
      ))
    ] }, chainId))
  ] });
}
function sourceText(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
function MetaItem({ label, children }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-0.5", children: [
    /* @__PURE__ */ jsx("dt", { className: "text-xs uppercase tracking-wide text-muted-foreground", children: label }),
    /* @__PURE__ */ jsx("dd", { className: "text-sm text-foreground", children })
  ] });
}
function ContractSourcePanel({
  client,
  chainId,
  address,
  block = null,
  className
}) {
  const [withBytecode, setWithBytecode] = React19.useState(false);
  const [tick, setTick] = React19.useState(0);
  const [state, setState] = React19.useState({ data: null, isLoading: Boolean(client), error: null });
  React19.useEffect(() => {
    if (!client) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }
    let alive = true;
    const ctrl = new AbortController();
    setState((prev) => ({ data: prev.data, isLoading: true, error: null }));
    client(
      { chainId, address },
      {
        includeBytecode: withBytecode,
        ...block != null ? { block: Number(block) } : {},
        signal: ctrl.signal
      }
    ).then((data2) => {
      if (alive) setState({ data: data2, isLoading: false, error: null });
    }).catch((err) => {
      if (alive) setState((prev) => ({ data: prev.data, isLoading: false, error: err }));
    });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [client, chainId, address, withBytecode, block, tick]);
  if (!client) {
    return /* @__PURE__ */ jsx("div", { className, children: /* @__PURE__ */ jsx(
      EmptyState,
      {
        size: "sm",
        title: "Source lookup not configured",
        description: "The host didn't wire a contract-source client. Verified source code and bytecode are unavailable here."
      }
    ) });
  }
  const { data, isLoading, error } = state;
  const refresh = () => setTick((n) => n + 1);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-3", children: [
      /* @__PURE__ */ jsx(
        Field,
        {
          label: /* @__PURE__ */ jsxs(Fragment, { children: [
            "Include bytecode",
            block != null ? ` at block ${block.toLocaleString()}` : ""
          ] }),
          orientation: "horizontal",
          reverse: true,
          labelClassName: "text-sm text-muted-foreground",
          children: /* @__PURE__ */ jsx(Switch, { checked: withBytecode, onCheckedChange: setWithBytecode })
        }
      ),
      /* @__PURE__ */ jsx(Button, { type: "button", variant: "outline", size: "sm", disabled: isLoading, onClick: refresh, children: isLoading ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Spinner, { size: "sm", className: "mr-2 text-current" }),
        "Fetching\u2026"
      ] }) : "Refresh" })
    ] }),
    error && data && /* @__PURE__ */ jsx(Alert, { variant: "destructive", children: /* @__PURE__ */ jsxs(AlertDescription, { className: "text-xs", children: [
      "Couldn't refresh: ",
      error.message,
      ". Showing the previously loaded result."
    ] }) }),
    isLoading && !data ? /* @__PURE__ */ jsxs("p", { className: "flex items-center gap-2 text-sm text-muted-foreground", children: [
      /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      " Fetching verified source\u2026"
    ] }) : error && !data ? /* @__PURE__ */ jsx(ErrorState, { error, onRetry: refresh }) : !data || !data.found ? /* @__PURE__ */ jsx(
      EmptyState,
      {
        size: "sm",
        title: "No verified source found",
        description: "No third-party verifier (Codeslaw, Etherscan, Blockscout, Sourcify) returned verified source for this address. It may be unverified, or you can retry."
      }
    ) : /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
        data.verified ? /* @__PURE__ */ jsx(Badge, { variant: "default", children: "Verified" }) : /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "Unverified" }),
        data.isProxy && /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: "Proxy" }),
        data.language && /* @__PURE__ */ jsx(Badge, { variant: "outline", children: data.language }),
        typeof data.confidence === "number" && /* @__PURE__ */ jsxs(Badge, { variant: "outline", children: [
          "confidence ",
          Math.round(data.confidence * 100),
          "%"
        ] })
      ] }),
      /* @__PURE__ */ jsxs("dl", { className: "grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3", children: [
        /* @__PURE__ */ jsx(MetaItem, { label: "Contract", children: data.contractName || "\u2014" }),
        /* @__PURE__ */ jsx(MetaItem, { label: "Compiler", children: data.compilerVersion || "\u2014" }),
        /* @__PURE__ */ jsx(MetaItem, { label: "License", children: data.license || "\u2014" }),
        /* @__PURE__ */ jsx(MetaItem, { label: "Optimization", children: data.optimizationUsed == null ? "\u2014" : data.optimizationUsed ? `Yes${data.runs != null ? ` (${data.runs} runs)` : ""}` : "No" }),
        /* @__PURE__ */ jsx(MetaItem, { label: "ABI entries", children: data.abiEntriesCount ?? "\u2014" }),
        /* @__PURE__ */ jsx(MetaItem, { label: "Source", children: data.origin })
      ] }),
      data.isProxy && data.implementationAddresses && data.implementationAddresses.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsx("span", { className: "text-xs uppercase tracking-wide text-muted-foreground", children: "Implementations" }),
        /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-1", children: data.implementationAddresses.map((impl) => /* @__PURE__ */ jsxs("li", { className: "flex items-center gap-2 font-mono text-xs", children: [
          /* @__PURE__ */ jsx("span", { className: "truncate", children: impl }),
          /* @__PURE__ */ jsx(CopyButton, { value: impl })
        ] }, impl)) })
      ] }),
      data.sourceAvailable && data.sourceCode ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: "Source code" }),
          /* @__PURE__ */ jsx(CopyButton, { value: sourceText(data.sourceCode) })
        ] }),
        /* @__PURE__ */ jsx("pre", { className: "max-h-96 overflow-auto rounded-md border bg-muted/40 p-3 text-xs leading-relaxed", children: /* @__PURE__ */ jsx("code", { children: sourceText(data.sourceCode) }) })
      ] }) : /* @__PURE__ */ jsx(Alert, { children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: "Metadata was found but the verifier didn't return source code for this contract." }) }),
      withBytecode && data.bytecode && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsxs("span", { className: "text-sm font-medium", children: [
            "Deployed bytecode",
            typeof data.bytecode.sizeBytes === "number" && /* @__PURE__ */ jsxs("span", { className: "ml-2 text-xs font-normal text-muted-foreground", children: [
              data.bytecode.sizeBytes.toLocaleString(),
              " bytes"
            ] })
          ] }),
          data.bytecode.bytecode && /* @__PURE__ */ jsx(CopyButton, { value: data.bytecode.bytecode })
        ] }),
        data.bytecode.error ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-destructive", children: [
          "Couldn't read bytecode: ",
          data.bytecode.error
        ] }) : data.bytecode.present === false ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "No code at this address (EOA or not deployed)." }) : data.bytecode.bytecode ? /* @__PURE__ */ jsx("pre", { className: "max-h-48 overflow-auto break-all rounded-md border bg-muted/40 p-3 font-mono text-xs", children: data.bytecode.bytecode }) : null
      ] })
    ] })
  ] });
}
var UNKNOWN = "(unknown)";
function argEntries(log6) {
  const args = log6.args;
  if (!args) return [];
  if (Array.isArray(args)) {
    return args.map((value, i) => ({ name: `#${i}`, value: formatReadValue(value) }));
  }
  return Object.entries(args).filter(([key]) => !/^\d+$/.test(key)).map(([name, value]) => ({ name, value: formatReadValue(value) }));
}
function LogRow({
  log: log6,
  chain,
  onDrilldown
}) {
  const entries = argEntries(log6);
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5 px-3 py-2.5", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-2", children: [
        /* @__PURE__ */ jsx(Badge, { variant: log6.decoded ? "secondary" : "outline", className: "whitespace-nowrap text-[10px]", children: log6.eventName ?? UNKNOWN }),
        log6.blockNumber != null && /* @__PURE__ */ jsxs("span", { className: "whitespace-nowrap text-xs text-muted-foreground", children: [
          "block ",
          log6.blockNumber.toString()
        ] })
      ] }),
      log6.transactionHash ? /* @__PURE__ */ jsx(
        HashDisplay,
        {
          hash: log6.transactionHash,
          chain,
          type: "tx",
          truncate: { head: 8, tail: 6 },
          onDrilldown
        }
      ) : null
    ] }),
    entries.length > 0 ? /* @__PURE__ */ jsx("dl", { className: "grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5", children: entries.map((entry) => /* @__PURE__ */ jsxs(React19.Fragment, { children: [
      /* @__PURE__ */ jsx("dt", { className: "font-mono text-[11px] text-muted-foreground", children: entry.name }),
      /* @__PURE__ */ jsx("dd", { className: "min-w-0", children: /* @__PURE__ */ jsx("code", { className: "line-clamp-3 break-all font-mono text-[11px]", title: entry.value, children: entry.value }) })
    ] }, entry.name)) }) : !log6.decoded ? /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-[11px] text-muted-foreground", children: [
      /* @__PURE__ */ jsx("code", { className: "break-all", children: log6.topic0 ?? "no topic0" }),
      log6.reason ? /* @__PURE__ */ jsxs("span", { className: "italic", children: [
        "\u2014 ",
        log6.reason
      ] }) : null,
      log6.topic0 ? /* @__PURE__ */ jsx(CopyButton, { value: log6.topic0 }) : null
    ] }) : null
  ] });
}
function EventLogPanel({
  contract,
  abi,
  query,
  reader,
  onDrilldown,
  className
}) {
  const { logs, summary, isPending, error, supported, fromCache, refetch } = useEventLogs(contract, abi, {
    reader,
    query
  });
  const [selected, setSelected] = React19.useState(/* @__PURE__ */ new Set());
  const toggle = React19.useCallback((name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);
  const active = React19.useMemo(() => {
    const names = new Set(summary.map((s) => s.name));
    return new Set([...selected].filter((name) => names.has(name)));
  }, [selected, summary]);
  const visible = React19.useMemo(
    () => active.size === 0 ? logs : logs.filter((log6) => active.has(log6.eventName ?? UNKNOWN)),
    [logs, active]
  );
  if (!supported) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Event logs unavailable",
        description: "The active reader can't fetch logs. Use the RPC reader or a backend reader with a logs transport.",
        size: "sm"
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold", children: "Events" }),
        /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-[10px]", children: logs.length }),
        fromCache && /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-[10px] text-muted-foreground", children: "cached" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-xs text-muted-foreground", children: [
        isPending && /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
        /* @__PURE__ */ jsx("button", { type: "button", className: "hover:text-foreground", onClick: refetch, children: "Refresh" })
      ] })
    ] }),
    summary.length > 0 && /* @__PURE__ */ jsxs("div", { className: "scroll-fade-x flex flex-wrap items-center gap-1.5", children: [
      summary.map((item) => {
        const on = active.has(item.name);
        return /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            onClick: () => toggle(item.name),
            className: cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors",
              on ? "border-primary bg-primary/10 text-foreground" : "text-muted-foreground hover:text-foreground"
            ),
            "aria-pressed": on,
            children: [
              /* @__PURE__ */ jsx("span", { className: "font-medium", children: item.name }),
              /* @__PURE__ */ jsx("span", { className: "tabular-nums text-muted-foreground", children: item.count })
            ]
          },
          item.name
        );
      }),
      active.size > 0 && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => setSelected(/* @__PURE__ */ new Set()),
          className: "px-1 text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline",
          children: "Show all"
        }
      )
    ] }),
    !abi && logs.length > 0 && /* @__PURE__ */ jsxs("p", { className: "rounded-md border border-risk-medium/40 bg-risk-medium/5 p-2 text-xs text-muted-foreground", children: [
      "No ABI resolved for this contract, so nothing below is decoded \u2014 each row shows its raw",
      " ",
      /* @__PURE__ */ jsx("code", { className: "font-mono", children: "topic0" }),
      ". Resolve or paste an ABI in the Edit tab to name these events."
    ] }),
    error ? /* @__PURE__ */ jsx(EmptyState, { title: "Couldn't load events", description: error.message, size: "sm" }) : visible.length === 0 ? /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: isPending ? "Loading events\u2026" : "No events",
        description: isPending ? void 0 : active.size > 0 ? "No logs match the selected events." : "No logs in the scanned block range.",
        size: "sm"
      }
    ) : /* @__PURE__ */ jsx("div", { className: "scroll-fade-y max-h-[480px] divide-y overflow-auto rounded-md border", children: visible.map((log6, i) => /* @__PURE__ */ jsx(LogRow, { log: log6, chain: contract.chainId, onDrilldown }, `${log6.transactionHash ?? "?"}-${log6.logIndex ?? i}`)) })
  ] });
}
var ABI_SCOPE_LABELS = {
  user: "Only me",
  tenant: "Tenant",
  org: "Organization",
  customer: "Entire customer"
};
function availableWriteScopes(identity) {
  if (!identity) return [];
  const out = [];
  if (identity.userId && identity.orgId && identity.tenantId) out.push("user");
  if (identity.orgId && identity.tenantId) out.push("tenant");
  if (identity.orgId) out.push("org");
  if (identity.customerId) out.push("customer");
  return out;
}
function ContractExplorer({
  contract,
  provider,
  identity,
  cache = sharedAbiCache,
  reader,
  contractStateClient,
  writer,
  proxyResolver,
  abiByAddress,
  contractSource,
  proxyOverrides,
  canEditProxy,
  canPurgeProxy,
  canIndexProxy,
  onBlockChange,
  className
}) {
  if (!isEVM(contract.chainId)) {
    return /* @__PURE__ */ jsx(Card, { className: cn("w-full", className), children: /* @__PURE__ */ jsx(CardContent, { className: "pt-6", children: /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Unsupported chain family",
        description: "The contract explorer is EVM-only (ABIs + EIP-1967 proxies). Bitcoin, Solana and other non-EVM chains need a family-specific explorer.",
        size: "sm"
      }
    ) }) });
  }
  return /* @__PURE__ */ jsx(
    ContractExplorerInner,
    {
      ...{ contract, provider, identity, cache, reader, contractStateClient, writer, proxyResolver, abiByAddress, contractSource, proxyOverrides, canEditProxy, canPurgeProxy, canIndexProxy, onBlockChange, className }
    }
  );
}
function ContractExplorerInner({
  contract,
  provider,
  identity,
  cache = sharedAbiCache,
  reader,
  contractStateClient,
  writer,
  proxyResolver,
  abiByAddress,
  contractSource,
  proxyOverrides,
  canEditProxy = false,
  canPurgeProxy = false,
  canIndexProxy = false,
  onBlockChange,
  className
}) {
  const { options, setOptions, reset } = useAbiOptions(identity);
  const inline = useInlineAbis(identity);
  const pins = usePinnedAbis(identity);
  const [blockText, setBlockText] = React19.useState(() => {
    if (contract.block?.blockNumber != null) return contract.block.blockNumber.toString();
    return contract.block?.blockTag ?? "latest";
  });
  const block = React19.useMemo(() => (0, contract_exports.parseBlock)(blockText), [blockText]);
  const [implOverride, setImplOverride] = React19.useState(null);
  const contractKey = `${contract.chainId.toLowerCase()}:${contract.address.toLowerCase()}`;
  if (useDepsChanged([contractKey])) setImplOverride(null);
  const pinnedBlock = block?.blockNumber ?? null;
  const setBlockPin = React19.useCallback(
    (next) => {
      setBlockText(next === null ? "" : next.toString());
      onBlockChange?.(next);
    },
    [onBlockChange]
  );
  const hostBlockKey = (0, contract_exports.blockKey)(contract.block);
  const hostBlockTextRef = React19.useRef("");
  hostBlockTextRef.current = contract.block?.blockNumber != null ? contract.block.blockNumber.toString() : contract.block?.blockTag ?? "";
  React19.useEffect(() => {
    setBlockText(
      (current) => (0, contract_exports.blockKey)((0, contract_exports.parseBlock)(current)) === hostBlockKey ? current : hostBlockTextRef.current
    );
  }, [hostBlockKey]);
  const rootRef = React19.useMemo(
    () => ({ chainId: contract.chainId, address: contract.address, block, label: contract.label }),
    [contract.chainId, contract.address, contract.label, block]
  );
  const {
    chain: proxyChain,
    isResolving: proxyResolving,
    error: proxyError,
    refetch: refetchProxy
  } = useProxyChain(rootRef, {
    discover: options.discoverProxies && options.useProxies,
    resolver: proxyResolver ?? void 0
  });
  const effectiveAddress = implOverride ?? (options.useProxies && proxyChain?.isProxy ? proxyChain.effectiveAddress : contract.address);
  const abiRef = React19.useMemo(
    () => ({ chainId: contract.chainId, address: effectiveAddress, block }),
    [contract.chainId, effectiveAddress, block]
  );
  const inlineEntries = inline.get({ chainId: contract.chainId, address: effectiveAddress });
  const { resolved, status, error, origin, refetch: refetchAbi } = useAbiResolver({
    ref: abiRef,
    provider,
    options,
    identity,
    inlineEntries,
    cache
  });
  const abi = resolved?.abi ?? null;
  const isGuessedAbi = Boolean(resolved?.isGuess) && origin !== "inline";
  const abiFailures = resolved?.failures;
  const abiMeta = resolved?.meta;
  const isSystemAbi = Boolean(abiMeta?.isDefault);
  const isInheritedAbi = Boolean(abiMeta?.inherited) && !isSystemAbi;
  const scopeRung = scopeRungLabel(abiMeta?.scope);
  const scopeLabel = origin === "inline" ? "Local override" : isGuessedAbi ? "Generic ERC guess" : isSystemAbi ? "System default" : isInheritedAbi ? scopeRung ? `Inherited \xB7 ${scopeRung}` : "Inherited" : abiMeta?.abiId ? scopeRung ? `Your ${scopeRung.toLowerCase()} scope` : "Your workspace" : null;
  const canWrite = Boolean(writer?.canEdit);
  const [persisting, setPersisting] = React19.useState(false);
  const [refreshing, setRefreshing] = React19.useState(false);
  const [refreshOpen, setRefreshOpen] = React19.useState(false);
  const writeScopes = React19.useMemo(() => availableWriteScopes(identity), [identity]);
  const [targetScope, setTargetScope] = React19.useState(void 0);
  React19.useEffect(() => {
    setTargetScope((cur) => {
      if (writeScopes.length <= 1) return void 0;
      return cur && writeScopes.includes(cur) ? cur : writeScopes[0];
    });
  }, [writeScopes]);
  const [nameDraft, setNameDraft] = React19.useState("");
  React19.useEffect(() => {
    setNameDraft(abiMeta?.contractName ?? "");
  }, [effectiveAddress, abiMeta?.abiId, abiMeta?.contractName]);
  const abiWindow = React19.useMemo(
    () => abiMeta?.validFromBlock != null && abiMeta.validFromBlock > 0 ? { validFromBlock: abiMeta.validFromBlock, validToBlock: abiMeta.validToBlock ?? null } : void 0,
    [abiMeta?.validFromBlock, abiMeta?.validToBlock]
  );
  const persistAbi = React19.useCallback(
    async (entries, scope, name) => {
      if (!writer) return;
      setPersisting(true);
      try {
        const trimmed = name?.trim();
        const result = await writer.save(
          { chainId: contract.chainId, address: effectiveAddress },
          entries,
          { scope, name: trimmed ? trimmed : void 0, ...abiWindow }
        );
        inline.remove({ chainId: contract.chainId, address: effectiveAddress });
        cache.invalidateRef(abiRef);
        refetchAbi();
        const where = scope ? ABI_SCOPE_LABELS[scope].toLowerCase() : "your workspace";
        notifySuccess(
          result.imported ? `Saved a copy for ${where}` : "ABI saved",
          { description: result.imported ? "The shared default is left unchanged." : void 0 }
        );
      } catch (err) {
        notifyError(err, { title: "Could not save the ABI." });
      } finally {
        setPersisting(false);
      }
    },
    [writer, contract.chainId, effectiveAddress, inline, cache, abiRef, refetchAbi, abiWindow]
  );
  const refreshAbi = React19.useCallback(async () => {
    if (!writer) return;
    setRefreshing(true);
    try {
      const result = await writer.refresh({ chainId: contract.chainId, address: effectiveAddress });
      cache.invalidateRef(abiRef);
      refetchAbi();
      if (!result.found) {
        const n = result.providersQueried;
        notifyWarning(
          "No ABI found",
          n === 0 ? "No ABI provider is configured for this chain, so nothing was queried. Paste the ABI in the Edit tab instead." : `${n ?? "The configured"} provider${n === 1 ? "" : "s"} had no ABI for this address. Paste one in the Edit tab instead.`
        );
        return;
      }
      if (result.persisted === false) {
        notifyInfo(
          "Found an ABI, but it wasn't saved",
          "Third-party write-back is disabled, so this result was not persisted."
        );
        return;
      }
      notifySuccess("ABI refreshed", {
        description: result.entriesCount ? `${result.entriesCount} entries from third-party explorers.` : void 0
      });
    } catch (err) {
      notifyError(err, { title: "Could not refresh the ABI." });
    } finally {
      setRefreshing(false);
    }
  }, [writer, contract.chainId, effectiveAddress, cache, abiRef, refetchAbi]);
  const abiSources = useAbiSources(
    { chainId: contract.chainId, address: effectiveAddress },
    abiByAddress,
    { enabled: Boolean(abiByAddress), block: pinnedBlock }
  );
  const [activeSel, setActiveSel] = React19.useState(null);
  const activeSourceId = React19.useMemo(() => {
    if (activeSel && activeSel.address.toLowerCase() === effectiveAddress.toLowerCase()) return activeSel.id;
    if (abiMeta?.abiId) {
      const match = abiSources.sources.find((s) => s.abiId === abiMeta.abiId);
      if (match) return abiSourceId(match);
    }
    if (isSystemAbi) {
      const match = abiSources.sources.find((s) => s.isDefault);
      if (match) return abiSourceId(match);
    }
    return null;
  }, [activeSel, effectiveAddress, abiMeta?.abiId, isSystemAbi, abiSources.sources]);
  const useSource = React19.useCallback(
    (entry) => {
      if (!entry.entries) return;
      inline.set({ chainId: contract.chainId, address: effectiveAddress }, entry.entries);
      cache.invalidateRef(abiRef);
      setActiveSel({ address: effectiveAddress, id: abiSourceId(entry) });
      notifySuccess("Using selected ABI", { description: abiSourceScopeLabel(entry) });
    },
    [inline, contract.chainId, effectiveAddress, cache, abiRef]
  );
  const callRef = React19.useMemo(
    () => ({ chainId: contract.chainId, address: contract.address, block }),
    [contract.chainId, contract.address, block]
  );
  const baseRefKey = React19.useMemo(() => (0, contract_exports.contractRefKey)(abiRef), [abiRef]);
  const pinIdForSource = React19.useCallback(
    (entry) => `${baseRefKey}#${abiSourceId(entry)}`,
    [baseRefKey]
  );
  const pinnedSourceIds = React19.useMemo(() => {
    const set = /* @__PURE__ */ new Set();
    for (const s of abiSources.sources) {
      if (pins.pinned.some((p) => p.id === pinIdForSource(s))) set.add(abiSourceId(s));
    }
    return set;
  }, [abiSources.sources, pins.pinned, pinIdForSource]);
  const toggleSourcePin = React19.useCallback(
    (entry) => {
      if (!entry.entries) return;
      const id = pinIdForSource(entry);
      if (pins.pinned.some((p) => p.id === id)) {
        pins.unpin(id);
        return;
      }
      const name = entry.name || resolved?.meta.contractName || `${abiRef.address.slice(0, 8)}\u2026`;
      pins.pin({
        id,
        label: `${name} \xB7 ${abiSourceScopeLabel(entry)}`,
        ref: abiRef,
        entries: entry.entries,
        meta: {
          abiId: entry.abiId,
          scope: entry.scope,
          isDefault: entry.isDefault,
          inherited: entry.inherited,
          contractName: entry.name ?? void 0
        }
      });
    },
    [pinIdForSource, pins, resolved, abiRef]
  );
  const activeSourceEntry = React19.useMemo(
    () => abiSources.sources.find((s) => abiSourceId(s) === activeSourceId) ?? null,
    [abiSources.sources, activeSourceId]
  );
  const currentPinId = `${baseRefKey}#${activeSourceId ?? "current"}`;
  const isCurrentPinned = React19.useMemo(
    () => pins.pinned.some((p) => p.id === currentPinId),
    [pins.pinned, currentPinId]
  );
  const togglePin = React19.useCallback(() => {
    if (isCurrentPinned) {
      pins.unpin(currentPinId);
      return;
    }
    if (!abi) return;
    const name = resolved?.meta.contractName ?? `${abiRef.address.slice(0, 8)}\u2026`;
    const scopeText = activeSourceEntry ? abiSourceScopeLabel(activeSourceEntry) : scopeLabel;
    pins.pin({
      id: currentPinId,
      label: scopeText ? `${name} \xB7 ${scopeText}` : name,
      ref: abiRef,
      entries: abi.entries,
      meta: resolved?.meta
    });
  }, [isCurrentPinned, currentPinId, abi, pins, resolved, abiRef, activeSourceEntry, scopeLabel]);
  const compareItems = React19.useMemo(
    () => pins.pinned.map((p) => ({ id: p.id, label: p.label, abi: (0, abi_exports.normalizeAbi)(p.entries) })),
    [pins.pinned]
  );
  const compareRefs = React19.useMemo(() => pins.pinned.map((p) => p.ref), [pins.pinned]);
  return /* @__PURE__ */ jsx(ContractReaderProvider, { reader, children: /* @__PURE__ */ jsx(ContractStateClientProvider, { client: contractStateClient ?? null, children: /* @__PURE__ */ jsxs(Card, { className: cn("w-full", className), children: [
    /* @__PURE__ */ jsxs(CardHeader, { className: "gap-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
          /* @__PURE__ */ jsx(ChainAvatar, { chain: contract.chainId }),
          /* @__PURE__ */ jsx(CardTitle, { className: "text-base", children: /* @__PURE__ */ jsx(AddressDisplay, { address: contract.address, chain: contract.chainId, label: resolved?.meta.contractName ?? void 0 }) }),
          scopeLabel && /* @__PURE__ */ jsx(
            Badge,
            {
              variant: isGuessedAbi ? "destructive" : isSystemAbi ? "secondary" : "outline",
              className: "text-[10px]",
              title: isGuessedAbi ? "No stored or verified ABI was found for this address. The well-known ERC interfaces are standing in." : void 0,
              children: scopeLabel
            }
          ),
          /* @__PURE__ */ jsx(
            WhyPopover,
            {
              title: "Why this ABI?",
              explanation: abiMeta?.explanation ?? null,
              evidence: abiMeta?.evidence ?? null,
              confidence: abiMeta?.confidence ?? null,
              lifecycle: abiMeta?.lifecycle ?? null
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(BlockPinControl, { id: "ce-block", value: pinnedBlock, onChange: setBlockPin }),
          canWrite && writer && /* @__PURE__ */ jsxs(AlertDialog, { open: refreshOpen, onOpenChange: (next) => !refreshing && setRefreshOpen(next), children: [
            /* @__PURE__ */ jsx(AlertDialogTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", disabled: refreshing, children: refreshing ? "Refreshing\u2026" : "Refresh" }) }),
            /* @__PURE__ */ jsxs(AlertDialogContent, { children: [
              /* @__PURE__ */ jsxs(AlertDialogHeader, { children: [
                /* @__PURE__ */ jsx(AlertDialogTitle, { children: "Re-fetch this ABI from third-party explorers?" }),
                /* @__PURE__ */ jsxs(AlertDialogDescription, { children: [
                  "This queries the configured explorers for",
                  " ",
                  /* @__PURE__ */ jsx("span", { className: "font-mono", children: effectiveAddress }),
                  " and, where write-back is enabled, replaces the shared system-default ABI for this contract \u2014 the one every tenant inherits when they have no override of their own. Your own overrides are not touched."
                ] })
              ] }),
              /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [
                /* @__PURE__ */ jsx(AlertDialogCancel, { disabled: refreshing, children: "Cancel" }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    type: "button",
                    disabled: refreshing,
                    onClick: () => {
                      setRefreshOpen(false);
                      void refreshAbi();
                    },
                    children: "Refresh ABI"
                  }
                )
              ] })
            ] })
          ] }),
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: isCurrentPinned ? "secondary" : "outline",
              disabled: !abi && !isCurrentPinned,
              onClick: togglePin,
              children: isCurrentPinned ? "Unpin" : "Pin ABI"
            }
          )
        ] })
      ] }),
      status === "loading" && /* @__PURE__ */ jsxs("p", { className: "flex items-center gap-2 text-xs text-muted-foreground", children: [
        /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
        " Resolving ABI\u2026"
      ] }),
      status === "error" && error && /* @__PURE__ */ jsx("p", { className: "text-xs text-destructive", children: error.message }),
      status === "empty" && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "No ABI found \u2014 paste one in the Edit tab." }),
      status === "ready" && abiFailures && abiFailures.length > 0 && /* @__PURE__ */ jsxs("p", { className: "text-xs text-risk-medium", children: [
        "ABI lookup failed \u2014 ",
        describeAbiFailures(abiFailures),
        " The ABI below came from a fallback source and may not be this contract's."
      ] }),
      status === "ready" && isGuessedAbi && /* @__PURE__ */ jsx("p", { className: "text-xs text-risk-medium", children: "No stored or verified ABI for this address \u2014 showing the generic ERC-20/721 interfaces. Reads may revert and sending is disabled." })
    ] }),
    /* @__PURE__ */ jsx(CardContent, { children: /* @__PURE__ */ jsxs(Tabs, { defaultValue: "fields", children: [
      /* @__PURE__ */ jsxs(TabsList, { className: "flex-wrap", children: [
        /* @__PURE__ */ jsx(TabsTrigger, { value: "fields", children: "Fields" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "onchain", children: "On-chain state" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "read", children: "Read" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "write", children: "Write" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "events", children: "Events" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "abi", children: "ABI" }),
        contractSource && /* @__PURE__ */ jsx(TabsTrigger, { value: "source", children: "Verified source" }),
        abiByAddress && /* @__PURE__ */ jsxs(TabsTrigger, { value: "sources", children: [
          "ABI sources",
          abiSources.sources.length > 0 && /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "ml-1.5 text-[10px]", children: abiSources.sources.length })
        ] }),
        /* @__PURE__ */ jsxs(TabsTrigger, { value: "proxy", children: [
          "Proxies",
          proxyChain?.isProxy && /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "ml-1.5 text-[10px]", children: proxyChain.hops.length })
        ] }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "state", children: "Compare blocks" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "edit", children: "Edit" }),
        /* @__PURE__ */ jsxs(TabsTrigger, { value: "compare", children: [
          "Compare ABIs",
          pins.pinned.length > 0 && /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "ml-1.5 text-[10px]", children: pins.pinned.length })
        ] }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "options", children: "Options" })
      ] }),
      /* @__PURE__ */ jsx(TabsContent, { value: "fields", className: "pt-3", children: /* @__PURE__ */ jsx(ContractFieldsGrid, { contract: callRef, abi, isGuess: isGuessedAbi, failures: abiFailures, onFetchAbi: canWrite && writer ? () => setRefreshOpen(true) : void 0, fetchingAbi: refreshing }) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "state", className: "pt-3", children: /* @__PURE__ */ jsx(ContractStateView, { chainId: contract.chainId, address: contract.address, abi }) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "onchain", className: "pt-3", children: /* @__PURE__ */ jsx(ContractStatePanel, { chainId: contract.chainId, address: contract.address, block }) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "read", className: "pt-3", children: /* @__PURE__ */ jsx(ReadPanel, { contract: callRef, abi, isGuess: isGuessedAbi, failures: abiFailures, onFetchAbi: canWrite && writer ? () => setRefreshOpen(true) : void 0, fetchingAbi: refreshing }) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "write", className: "pt-3", children: /* @__PURE__ */ jsx(WritePanel, { contract: callRef, abi, isGuess: isGuessedAbi, failures: abiFailures, onFetchAbi: canWrite && writer ? () => setRefreshOpen(true) : void 0, fetchingAbi: refreshing }) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "events", className: "pt-3", children: /* @__PURE__ */ jsx(EventLogPanel, { contract: callRef, abi }) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "abi", className: "pt-3", children: /* @__PURE__ */ jsx(AbiViewer, { abi }) }),
      contractSource && /* @__PURE__ */ jsx(TabsContent, { value: "source", className: "pt-3", children: /* @__PURE__ */ jsx(
        ContractSourcePanel,
        {
          client: contractSource,
          chainId: contract.chainId,
          address: effectiveAddress,
          block: pinnedBlock
        }
      ) }),
      abiByAddress && /* @__PURE__ */ jsx(TabsContent, { value: "sources", className: "pt-3", children: /* @__PURE__ */ jsx(
        AbiSourcesPanel,
        {
          sources: abiSources.sources,
          isLoading: abiSources.isLoading,
          error: abiSources.error,
          onRetry: abiSources.refetch,
          activeSourceId,
          pinnedIds: pinnedSourceIds,
          block: pinnedBlock,
          onUse: useSource,
          onTogglePin: toggleSourcePin
        }
      ) }),
      /* @__PURE__ */ jsxs(TabsContent, { value: "proxy", className: "space-y-4 pt-3", children: [
        /* @__PURE__ */ jsx(
          ProxyChainView,
          {
            chain: proxyChain,
            chainId: contract.chainId,
            isResolving: proxyResolving,
            error: proxyError,
            onRetry: refetchProxy,
            onSelectImplementation: setImplOverride
          }
        ),
        proxyOverrides && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Separator, {}),
          /* @__PURE__ */ jsx(
            ProxyOverridePanel,
            {
              chainId: contract.chainId,
              address: contract.address,
              client: proxyOverrides,
              canEdit: canEditProxy,
              canPurge: canPurgeProxy,
              canIndex: canIndexProxy,
              writeScopes,
              block: pinnedBlock,
              onBlockChange: setBlockPin
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsx(TabsContent, { value: "edit", className: "pt-3", children: /* @__PURE__ */ jsx(
        InlineAbiEditor,
        {
          initialEntries: isGuessedAbi ? null : abi?.entries ?? null,
          hasOverride: inlineEntries != null,
          onSave: (entries) => {
            inline.set({ chainId: contract.chainId, address: effectiveAddress }, entries);
            cache.invalidateRef(abiRef);
          },
          onClear: () => {
            inline.remove({ chainId: contract.chainId, address: effectiveAddress });
            cache.invalidateRef(abiRef);
          },
          onPersist: canWrite && writer ? (entries) => void persistAbi(entries, targetScope, nameDraft) : void 0,
          persistBusy: persisting,
          name: nameDraft,
          onNameChange: canWrite && writer ? setNameDraft : void 0,
          scopeOptions: writeScopes.length > 1 ? writeScopes.map((s) => ({ value: s, label: ABI_SCOPE_LABELS[s] })) : void 0,
          scope: targetScope,
          onScopeChange: (value) => setTargetScope(value),
          notice: abiWindow ? /* @__PURE__ */ jsx(Alert, { children: /* @__PURE__ */ jsxs(AlertDescription, { className: "text-xs", children: [
            "This ABI is the version for",
            " ",
            /* @__PURE__ */ jsx("span", { className: "font-mono", children: abiWindow.validToBlock == null ? `blocks ${abiWindow.validFromBlock.toLocaleString()}+` : `blocks ${abiWindow.validFromBlock.toLocaleString()}\u2013${(abiWindow.validToBlock - 1).toLocaleString()}` }),
            ". Saving updates that version, not the contract's current ABI."
          ] }) }) : isGuessedAbi ? /* @__PURE__ */ jsx(Alert, { className: "border-risk-medium/40 bg-risk-medium/5", children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: "No ABI was found for this address, so the editor starts empty rather than pre-filled with the generic ERC-20/721 stand-in \u2014 saving that would register a guess as this contract's ABI for everyone at your scope. Paste the real ABI here." }) }) : canWrite && (isSystemAbi || isInheritedAbi) ? /* @__PURE__ */ jsx(Alert, { children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: isSystemAbi ? "This is a shared system ABI. Saving creates a separate copy at the scope you pick \u2014 the shared default is left unchanged." : "This ABI is inherited from a broader scope. Saving creates a copy owned at the scope you pick." }) }) : void 0
        }
      ) }),
      /* @__PURE__ */ jsxs(TabsContent, { value: "compare", className: "space-y-4 pt-3", children: [
        pins.pinned.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
          pins.pinned.map((p) => /* @__PURE__ */ jsxs(
            "span",
            {
              className: "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
              children: [
                /* @__PURE__ */ jsx("span", { className: "max-w-[12rem] truncate", children: p.label }),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    "aria-label": `Unpin ${p.label}`,
                    title: "Unpin",
                    className: "leading-none text-muted-foreground hover:text-foreground",
                    onClick: () => pins.unpin(p.id),
                    children: "\xD7"
                  }
                )
              ]
            },
            p.id
          )),
          /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: pins.clear, children: "Clear all" })
        ] }),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("h4", { className: "mb-2 text-sm font-semibold", children: "Interface diff (pinned ABIs)" }),
          /* @__PURE__ */ jsx(CompareAbisView, { items: compareItems })
        ] }),
        /* @__PURE__ */ jsx(Separator, {}),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("h4", { className: "mb-2 text-sm font-semibold", children: "Field values (pinned refs)" }),
          /* @__PURE__ */ jsx(CompareFieldsView, { refs: compareRefs, abi })
        ] })
      ] }),
      /* @__PURE__ */ jsx(TabsContent, { value: "options", className: "pt-3", children: /* @__PURE__ */ jsx(
        AbiSourcePanel,
        {
          options,
          onChange: setOptions,
          onReset: reset,
          origin,
          meta: resolved?.meta ?? null,
          isGuess: isGuessedAbi
        }
      ) })
    ] }) })
  ] }) }) });
}

// src/relationships.ts
var ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
function isAddressLike(value) {
  return typeof value === "string" && /^0x[0-9a-fA-F]{40}$/.test(value) && value.toLowerCase() !== ZERO_ADDRESS;
}
function relatedAddressesFromFields(fields) {
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  const push = (field, address, via) => {
    const key = address.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ field, address, via });
  };
  for (const { fn, outcome } of fields) {
    if (!outcome || outcome.status !== "success") continue;
    const outputs = fn.outputs ?? [];
    if (outputs.length !== 1) continue;
    const type = outputs[0].type;
    const data = outcome.data;
    if (type === "address" && isAddressLike(data)) {
      push(fn.name, data, "address");
    } else if (type === "address[]" && Array.isArray(data)) {
      for (const item of data) if (isAddressLike(item)) push(fn.name, item, "address[]");
    }
  }
  return out;
}

// src/proxy/detect.ts
var KNOWN_PROXY_SLOTS = [
  {
    id: "eip1967.impl",
    label: "Implementation",
    slot: contract_exports.EIP1967_IMPL_SLOT,
    decode: "address",
    description: "EIP-1967 implementation slot (transparent / UUPS)."
  },
  {
    id: "eip1967.beacon",
    label: "Beacon",
    slot: contract_exports.EIP1967_BEACON_SLOT,
    decode: "address",
    description: "EIP-1967 beacon slot \u2014 beacon returns the implementation."
  },
  {
    id: "eip1967.admin",
    label: "Admin",
    slot: contract_exports.EIP1967_ADMIN_SLOT,
    decode: "address",
    description: "EIP-1967 admin slot (present on transparent proxies)."
  },
  {
    id: "oz.legacy.impl",
    label: "Legacy implementation",
    slot: contract_exports.OZ_LEGACY_IMPL_SLOT,
    decode: "address",
    description: "Legacy OpenZeppelin (zeppelinos) implementation slot."
  }
];
var UUPS_ABI = [
  { type: "function", name: "proxiableUUID", stateMutability: "view", inputs: [], outputs: [{ type: "bytes32" }] }
];
var DIAMOND_LOUPE_ABI = [
  { type: "function", name: "facetAddresses", stateMutability: "view", inputs: [], outputs: [{ type: "address[]" }] },
  {
    type: "function",
    name: "facets",
    stateMutability: "view",
    inputs: [],
    outputs: [
      {
        type: "tuple[]",
        components: [
          { name: "facetAddress", type: "address" },
          { name: "functionSelectors", type: "bytes4[]" }
        ]
      }
    ]
  }
];
function classifyProxyKind(signals) {
  if (signals.supportsDiamondLoupe) return contract_exports.ProxyKind.DIAMOND;
  if (signals.hasBeaconSlot) return contract_exports.ProxyKind.BEACON;
  if (signals.hasImplSlot) {
    if (signals.hasAdminSlot) return contract_exports.ProxyKind.TRANSPARENT;
    if (signals.supportsProxiableUUID) return contract_exports.ProxyKind.UUPS;
    return contract_exports.ProxyKind.EIP1967;
  }
  if (signals.hasLegacySlot) return contract_exports.ProxyKind.CUSTOM;
  return contract_exports.ProxyKind.UNKNOWN;
}

export { ABI_CACHE_MAX_ENTRIES, ABI_CACHE_TTL_MS, ABI_OPTIONS_KEY, ABI_OPTIONS_NAME, ABI_SOURCE_LABELS, ALL_ABI_SOURCES, AbiCache, AbiSourceKind, AbiSourcePanel, AbiSourcesPanel, AbiSourcesUnavailableError, AbiViewer, AddressIdentityChip, BalanceChangesPanel, CONTRACT_EXPLORER_PACKAGE, CompareAbisView, CompareFieldsView, ContractExplorer, ContractFieldsGrid, ContractReaderProvider, ContractSourcePanel, ContractStateClientProvider, ContractStateHighlighter, ContractStateNodeCard, ContractStatePanel, ContractStateTree, ContractStateView, DEFAULT_ABI_FETCH_OPTIONS, DIAMOND_LOUPE_ABI, EVENT_LOG_CACHE_MAX_ENTRIES, EVENT_LOG_CACHE_TTL_MS, EventLogCache, EventLogPanel, FunctionForm, GuessedAbiNotice, INLINE_ABIS_KEY, INLINE_ABIS_NAME, InlineAbiEditor, KNOWN_PROXY_SLOTS, NoAbiState, PINNED_ABIS_KEY, PINNED_ABIS_LIMIT, PINNED_ABIS_NAME, PROVIDER_ABI_SOURCES, PROXY_KIND_LABELS, ProxyChainView, ProxyOverridePanel, ReadPanel, TransactionInsightClientProvider, UUPS_ABI, WritePanel, abiCacheKey, abiSourceId, abiSourceLabel, abiSourceScopeLabel, asText, blockToBlockSpec, buildArgs, buildContractStateRequest, changeCount, changedMultiRows, changedRows, classifyProxyKind, coerceArg, createApiAbiByAddressClient, createApiAbiProvider, createApiAbiWriter, createApiContractSourceClient, createApiContractStateClient, createApiProxyResolver, createApiTransactionInsightClient, createCompositeAbiProvider, createContractStateClient, createDefaultAbiProvider, createInMemoryAbiProvider, createTransactionInsightClient, createValidatingClient, defaultContractReader, describeAbiFailures, diffContractState, diffContractStates, eventLogCacheKey, formatReadValue, identityKey, identityKeyLookup, moveAbiSource, omittedReadsNote, optionsKey, providerSourceOrder, proxyKindLabel, relatedAddressesFromFields, sharedAbiCache, sharedEventLogCache, useAbiOptions, useAbiResolver, useAbiSources, useAbiStorage, useContractFields, useContractReader, useContractState, useContractStateClient, useEventLogs, useInlineAbis, usePinnedAbis, useProxyChain, useReaderRead, useTokenIdentity, useTransactionInsight, useTransactionInsightClient, withProviderOrder };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map